import {
  ArrowPathIcon,
  BoltIcon,
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  ClockIcon,
  CogIcon,
  DocumentTextIcon,
  ExclamationCircleIcon,
  LightBulbIcon,
  PlusIcon,
  ServerIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import specKitTaskData from '../../data/specKitTasks.json';
import { apiService } from '../../services/api';

interface Task {
  id: string;
  title: string;
  body: string;
  status: string;
  priority: number;
  assignee: string | null;
  labels: string[];
  created_at: string;
  updated_at: string;
}

interface KPIs {
  project_age_days: number;
  total_tasks: number;
  completion_rate: number;
  active_tasks: number;
  blocked_tasks: number;
  total_errors: number;
  open_errors: number;
  error_rate: number;
  total_runs: number;
  success_rate: number;
  avg_run_duration: number;
  health_score: number;
}

interface TrackerOverview {
  project: {
    name: string;
    goal: string;
    status: string;
    owner: string;
    id: string;
    created_at: string;
    updated_at: string;
  };
  latest_self_check: {
    items: Array<{
      name: string;
      passed: boolean;
      details: string;
    }>;
    score: number;
    ran_at: string;
  } | null;
  latest_env_snapshot: Record<string, unknown> | null;
  next_milestone: Record<string, unknown> | null;
  recent_runs: Array<{
    id: string;
    agent: string;
    summary: string;
    started_at: string;
    status: string;
  }>;
  open_errors: Array<{
    id: string;
    title: string;
  }>;
  active_tasks: Task[];
}

interface SpecKitTaskDocumentation {
  label: string;
  path: string;
}

interface SpecKitTaskLink {
  label: string;
  url?: string;
}

interface SpecKitTask {
  id: string;
  title: string;
  category?: string;
  status?: string;
  summary?: string;
  command?: string;
  spec?: string;
  documentation?: SpecKitTaskDocumentation[];
  links?: SpecKitTaskLink[];
}

interface SpecKitTaskExport {
  generatedAt?: string;
  metadata?: Record<string, unknown>;
  tasks: SpecKitTask[];
}

// Merge exported Spec-Kit JSON while ensuring we only define `tasks` once to avoid TS2783
const specKitExportData: SpecKitTaskExport = {
  ...(specKitTaskData as SpecKitTaskExport),
  // Provide a safe fallback if tasks is missing or null
  tasks: (specKitTaskData as SpecKitTaskExport)?.tasks ?? [],
};

const SPEC_KIT_STATUS_META: Record<string, { label: string; badgeClass: string }> = {
  planned: { label: 'Planned', badgeClass: 'bg-gray-100 text-gray-700' },
  in_progress: { label: 'In Progress', badgeClass: 'bg-blue-100 text-blue-700' },
  done: { label: 'Complete', badgeClass: 'bg-emerald-100 text-emerald-700' },
  blocked: { label: 'Blocked', badgeClass: 'bg-rose-100 text-rose-700' },
};

const getSpecKitStatusMeta = (status?: string) => {
  const normalized = (status ?? 'planned').toLowerCase();
  return SPEC_KIT_STATUS_META[normalized] ?? SPEC_KIT_STATUS_META.planned;
};

const aggregateSpecKitStatus = (tasks: SpecKitTask[]) => {
  const normalized = tasks.map((task) => (task.status ?? 'planned').toLowerCase());
  if (normalized.includes('blocked')) {
    return 'blocked';
  }
  if (normalized.every((status) => status === 'done')) {
    return 'done';
  }
  if (normalized.includes('in_progress')) {
    return 'in_progress';
  }
  return 'planned';
};

const groupSpecKitTasksByCategory = (tasks: SpecKitTask[]): Array<[string, SpecKitTask[]]> => {
  const grouped = tasks.reduce<Record<string, SpecKitTask[]>>((acc, task) => {
    const category = task.category ?? 'uncategorized';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(task);
    return acc;
  }, {});

  return Object.entries(grouped).sort((a, b) => a[0].localeCompare(b[0]));
};

const formatSpecKitTimestamp = (timestamp?: string) => {
  if (!timestamp) {
    return 'N/A';
  }
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) {
    return timestamp;
  }
  return date.toLocaleString();
};

const isExternalLink = (url?: string) => !!url && /^https?:\/\//.test(url);

const SuperTrackerPage: React.FC = () => {
  // Session log form state
  const [showSessionLogForm, setShowSessionLogForm] = useState(false);
  const [sessionLogAgent, setSessionLogAgent] = useState('');
  const [sessionLogSummary, setSessionLogSummary] = useState('');

  const queryClient = useQueryClient();

  const {
    data: overview,
    isLoading,
    error,
    refetch,
    isFetching: isOverviewFetching,
    dataUpdatedAt: overviewUpdatedAt,
  } = useQuery<TrackerOverview>({
    queryKey: ['tracker-overview'],
    queryFn: async (): Promise<TrackerOverview> => {
      return await apiService.get('/tracker/overview');
    },
    refetchInterval: 30000, // Refresh every 30 seconds
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    staleTime: 0,
  });

  const {
    data: kpis,
    refetch: refetchKpis,
    isFetching: isKpisFetching,
    dataUpdatedAt: kpisUpdatedAt,
  } = useQuery<KPIs>({
    queryKey: ['tracker-kpis'],
    queryFn: async (): Promise<KPIs> => {
      return await apiService.get('/tracker/kpis');
    },
    refetchInterval: 60000, // Refresh every minute
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    staleTime: 0,
  });

  const specKitTasks = specKitExportData.tasks ?? [];
  const specKitCategoryEntries = groupSpecKitTasksByCategory(specKitTasks);
  const specKitMetadata = (specKitExportData.metadata ?? {}) as {
    updated?: string;
    version?: number;
  };
  const specKitGeneratedAt = formatSpecKitTimestamp(specKitExportData.generatedAt);
  const specKitUpdatedAt = specKitMetadata.updated
    ? formatSpecKitTimestamp(specKitMetadata.updated)
    : null;
  const lastUpdatedAt = Math.max(overviewUpdatedAt || 0, kpisUpdatedAt || 0);

  // Session log mutation
  const logSessionMutation = useMutation({
    mutationFn: async (sessionData: { agent: string; summary: string }) => {
      // API expects query parameters, not JSON body
      const params = new URLSearchParams({
        agent: sessionData.agent,
        summary: sessionData.summary,
      });
      return await apiService.post(`/tracker/runs?${params.toString()}`, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tracker-overview'] });
      setShowSessionLogForm(false);
      setSessionLogAgent('');
      setSessionLogSummary('');
    },
  });

  const handleLogSession = () => {
    if (sessionLogAgent && sessionLogSummary) {
      logSessionMutation.mutate({ agent: sessionLogAgent, summary: sessionLogSummary });
    }
  };

  const runSelfCheck = async () => {
    try {
      await apiService.post('/tracker/self-check');
      await refetch();
      await refetchKpis();
    } catch (error) {
      console.error('Failed to run self-check:', error);
    }
  };

  const handleRefresh = async () => {
    await Promise.all([refetch(), refetchKpis()]);
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-32 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <ExclamationCircleIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error loading tracker data</h3>
              <p className="mt-2 text-sm text-red-700">
                {error instanceof Error ? error.message : 'Unknown error'}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'text-green-600 bg-green-50';
      case 'planning':
        return 'text-yellow-600 bg-yellow-50';
      case 'on_hold':
        return 'text-red-600 bg-red-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const getHealthColor = (score: number) => {
    if (score >= 0.8) return 'text-green-600';
    if (score >= 0.6) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">SuperTracker</h1>
          <p className="text-gray-600">Project monitoring and health dashboard</p>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500">
            <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
              {isOverviewFetching || isKpisFetching ? 'Live: Updating…' : 'Live'}
            </span>
            <span>
              Last updated: {lastUpdatedAt ? new Date(lastUpdatedAt).toLocaleTimeString() : '—'}
            </span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={handleRefresh}
            className="inline-flex items-center px-4 py-2 border border-gray-200 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50"
          >
            <ArrowPathIcon
              className={`h-4 w-4 mr-2 ${isOverviewFetching || isKpisFetching ? 'animate-spin' : ''}`}
            />
            Refresh Now
          </button>
          <button
            onClick={runSelfCheck}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <CogIcon className="h-4 w-4 mr-2" />
            Run Self-Check
          </button>
        </div>
      </div>

      {/* Tracker Navigation */}
      <div className="bg-white rounded-lg shadow p-4">
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Tracker Modules</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <NavLink
            to="/tracker/roadmap"
            className={({ isActive }) =>
              `flex flex-col items-center p-3 rounded-lg border-2 transition-colors ${
                isActive
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-200 hover:border-gray-300 text-gray-600 hover:text-gray-900'
              }`
            }
          >
            <DocumentTextIcon className="h-6 w-6 mb-1" />
            <span className="text-xs font-medium">Roadmap</span>
          </NavLink>

          <NavLink
            to="/tracker/tasks"
            className={({ isActive }) =>
              `flex flex-col items-center p-3 rounded-lg border-2 transition-colors ${
                isActive
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-200 hover:border-gray-300 text-gray-600 hover:text-gray-900'
              }`
            }
          >
            <CheckCircleIcon className="h-6 w-6 mb-1" />
            <span className="text-xs font-medium">Tasks</span>
          </NavLink>

          <NavLink
            to="/tracker/errors"
            className={({ isActive }) =>
              `flex flex-col items-center p-3 rounded-lg border-2 transition-colors ${
                isActive
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-200 hover:border-gray-300 text-gray-600 hover:text-gray-900'
              }`
            }
          >
            <ExclamationCircleIcon className="h-6 w-6 mb-1" />
            <span className="text-xs font-medium">Errors</span>
          </NavLink>

          <NavLink
            to="/tracker/decisions"
            className={({ isActive }) =>
              `flex flex-col items-center p-3 rounded-lg border-2 transition-colors ${
                isActive
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-200 hover:border-gray-300 text-gray-600 hover:text-gray-900'
              }`
            }
          >
            <LightBulbIcon className="h-6 w-6 mb-1" />
            <span className="text-xs font-medium">Decisions</span>
          </NavLink>

          <NavLink
            to="/tracker/health"
            className={({ isActive }) =>
              `flex flex-col items-center p-3 rounded-lg border-2 transition-colors ${
                isActive
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-200 hover:border-gray-300 text-gray-600 hover:text-gray-900'
              }`
            }
          >
            <ServerIcon className="h-6 w-6 mb-1" />
            <span className="text-xs font-medium">Health</span>
          </NavLink>

          <NavLink
            to="/tracker/runbooks"
            className={({ isActive }) =>
              `flex flex-col items-center p-3 rounded-lg border-2 transition-colors ${
                isActive
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-200 hover:border-gray-300 text-gray-600 hover:text-gray-900'
              }`
            }
          >
            <CogIcon className="h-6 w-6 mb-1" />
            <span className="text-xs font-medium">Runbooks</span>
          </NavLink>

          <NavLink
            to="/tracker/plans"
            className={({ isActive }) =>
              `flex flex-col items-center p-3 rounded-lg border-2 transition-colors ${
                isActive
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-200 hover:border-gray-300 text-gray-600 hover:text-gray-900'
              }`
            }
          >
            <DocumentTextIcon className="h-6 w-6 mb-1" />
            <span className="text-xs font-medium">Plans</span>
          </NavLink>
        </div>
      </div>

      {/* Spec-Kit Automation Overview */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Spec-Kit Automation</h2>
            <p className="text-sm text-gray-600">
              Tasks exported from GitHub Spec-Kit to keep documentation, automation, and
              SuperTracker in sync.
            </p>
          </div>
          <div className="text-xs text-gray-500 text-right space-y-1">
            <div>Generated: {specKitGeneratedAt}</div>
            {specKitUpdatedAt ? <div>Manifest updated: {specKitUpdatedAt}</div> : null}
            {typeof specKitMetadata.version === 'number' ? (
              <div>Version: {specKitMetadata.version}</div>
            ) : null}
          </div>
        </div>

        {specKitCategoryEntries.length > 0 ? (
          <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
            {specKitCategoryEntries.map(([category, tasks]) => {
              const aggregateStatus = aggregateSpecKitStatus(tasks);
              const aggregateMeta = getSpecKitStatusMeta(aggregateStatus);

              return (
                <div key={category} className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 capitalize">
                        {category.replace(/[-_]/g, ' ')}
                      </h3>
                      <p className="text-xs text-gray-600">
                        {tasks.length} automation task{tasks.length !== 1 ? 's' : ''}
                      </p>
                    </div>
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-semibold ${aggregateMeta.badgeClass}`}
                    >
                      {aggregateMeta.label}
                    </span>
                  </div>

                  <ul className="mt-3 space-y-3">
                    {tasks.map((task) => {
                      const statusMeta = getSpecKitStatusMeta(task.status);
                      return (
                        <li
                          key={task.id}
                          className="rounded-md border border-gray-200 bg-white p-3 shadow-sm"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <p className="text-sm font-medium text-gray-900">{task.title}</p>
                              {task.summary ? (
                                <p className="mt-1 text-sm text-gray-600">{task.summary}</p>
                              ) : null}
                            </div>
                            <span
                              className={`px-2 py-0.5 rounded-full text-xs font-semibold ${statusMeta.badgeClass}`}
                            >
                              {statusMeta.label}
                            </span>
                          </div>

                          {task.spec ? (
                            <div className="mt-2 text-xs text-indigo-700">
                              Spec ID:{' '}
                              <code className="font-mono text-indigo-700">{task.spec}</code>
                            </div>
                          ) : null}

                          {task.command ? (
                            <div className="mt-3">
                              <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                Command
                              </span>
                              <code className="mt-1 block rounded bg-gray-900/5 px-2 py-1 text-xs font-mono text-gray-800">
                                {task.command}
                              </code>
                            </div>
                          ) : null}

                          {task.documentation && task.documentation.length > 0 ? (
                            <div className="mt-3">
                              <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                Documentation
                              </span>
                              <ul className="mt-1 space-y-1">
                                {task.documentation.map((doc) => (
                                  <li
                                    key={`${task.id}-${doc.path}`}
                                    className="flex items-center gap-2 text-xs text-gray-600"
                                  >
                                    <DocumentTextIcon className="h-3.5 w-3.5 text-gray-400" />
                                    <span className="font-medium text-gray-700">{doc.label}</span>
                                    <code className="rounded bg-gray-100 px-1 py-0.5 text-[0.65rem] text-gray-600">
                                      {doc.path}
                                    </code>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ) : null}

                          {task.links && task.links.length > 0 ? (
                            <div className="mt-3">
                              <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                Resources
                              </span>
                              <ul className="mt-1 space-y-1">
                                {task.links.map((link) => (
                                  <li
                                    key={`${task.id}-${link.label}`}
                                    className="text-xs text-gray-600"
                                  >
                                    {link.url && isExternalLink(link.url) ? (
                                      <a
                                        href={link.url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-indigo-600 hover:text-indigo-800 hover:underline underline-offset-2"
                                      >
                                        {link.label}
                                      </a>
                                    ) : (
                                      <span>
                                        {link.label}
                                        {link.url ? (
                                          <>
                                            :{' '}
                                            <code className="rounded bg-gray-100 px-1 py-0.5 text-[0.65rem] text-gray-600">
                                              {link.url}
                                            </code>
                                          </>
                                        ) : null}
                                      </span>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ) : null}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="mt-4 rounded-md border border-dashed border-gray-200 bg-gray-50 p-6 text-sm text-gray-600">
            No Spec-Kit automation tasks have been defined yet. Run{' '}
            <code className="font-mono">make spec-export</code> after adding entries to{' '}
            <code className="font-mono">specs/&lt;area&gt;/&lt;feature&gt;/tasks.md</code> to
            refresh this feed.
          </div>
        )}
      </div>

      {/* Enhanced KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Project Status */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircleIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Project Status</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(overview?.project.status || '')}`}
                    >
                      {overview?.project.status}
                    </span>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* System Health */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ServerIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">System Health</dt>
                  <dd className="text-lg font-medium">
                    {kpis ? (
                      <span
                        className={
                          kpis.health_score >= 80
                            ? 'text-green-600'
                            : kpis.health_score >= 60
                              ? 'text-yellow-600'
                              : 'text-red-600'
                        }
                      >
                        {kpis.health_score}%
                      </span>
                    ) : overview?.latest_self_check ? (
                      <span className={getHealthColor(overview.latest_self_check.score)}>
                        {Math.round(overview.latest_self_check.score * 100)}%
                      </span>
                    ) : (
                      <span className="text-gray-400">No data</span>
                    )}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Task Completion Rate */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ChartBarIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Task Completion</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {kpis
                      ? `${kpis.completion_rate}%`
                      : `${overview?.active_tasks.length || 0} active`}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Error Rate */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ExclamationCircleIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Error Rate</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {kpis ? `${kpis.error_rate}%` : `${overview?.open_errors.length || 0} open`}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Project Age */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ClockIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Project Age</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {kpis ? `${kpis.project_age_days} days` : 'N/A'}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Run Success Rate */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CogIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Run Success</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {kpis ? `${kpis.success_rate}%` : 'N/A'}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Active Tasks */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ClockIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Active Tasks</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {overview?.active_tasks.length || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Blocked Tasks */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ExclamationCircleIcon className="h-6 w-6 text-red-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Blocked Tasks</dt>
                  <dd className="text-lg font-medium text-gray-900">{kpis?.blocked_tasks ?? 0}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Commits Today */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <DocumentTextIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Runs</dt>
                  <dd className="text-lg font-medium text-gray-900">{kpis?.total_runs ?? 0}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* System Uptime */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ServerIcon className="h-6 w-6 text-green-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Health Score</dt>
                  <dd className="text-lg font-medium text-green-600">
                    {kpis?.health_score ?? 50}%
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Features This Week */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <SparklesIcon className="h-6 w-6 text-yellow-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Completion Rate</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {kpis?.completion_rate ?? 0}%
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Performance Score */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <BoltIcon className="h-6 w-6 text-blue-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Success Rate</dt>
                  <dd className="text-lg font-medium text-blue-600">{kpis?.success_rate ?? 0}%</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Session Status Section - Dynamic from API */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 shadow rounded-lg border-l-4 border-indigo-400">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <LightBulbIcon className="h-6 w-6 text-indigo-600" />
              </div>
              <h3 className="ml-3 text-lg leading-6 font-medium text-gray-900">
                Session Status -{' '}
                {new Date().toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </h3>
            </div>
            <button
              onClick={() => setShowSessionLogForm(!showSessionLogForm)}
              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200"
            >
              <PlusIcon className="h-4 w-4 mr-1" />
              Log Session
            </button>
          </div>

          {/* Session Log Form */}
          {showSessionLogForm && (
            <div className="mb-6 p-4 bg-white rounded-lg border border-indigo-200">
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Log Agent Session</h4>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Agent Name</label>
                  <input
                    type="text"
                    value={sessionLogAgent}
                    onChange={(e) => setSessionLogAgent(e.target.value)}
                    placeholder="Claude, GPT, Copilot, etc."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Session Summary
                  </label>
                  <textarea
                    value={sessionLogSummary}
                    onChange={(e) => setSessionLogSummary(e.target.value)}
                    placeholder="What was accomplished in this session..."
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleLogSession}
                    disabled={!sessionLogAgent || !sessionLogSummary}
                    className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
                  >
                    Save Session Log
                  </button>
                  <button
                    onClick={() => setShowSessionLogForm(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="mb-4 rounded-md border border-indigo-200 bg-white px-3 py-2 text-xs text-gray-600">
            <span className="font-semibold text-indigo-700">Session hygiene:</span> log updates in
            docs/SESSION_STORYBOOK.md, keep sprint docs aligned, and note Marketplace Integrations
            progress.
            <div className="mt-1 text-gray-500">
              Latest sync: Apr 9, 2026 — 135-file backlog triaged into 13 clean commits and
              deployed; code-reviewer caught 2 critical entitlement bugs pre-commit (stocking guide
              + marketplaces would have 403'd UAB); security hardening + SaaS entitlement gating +
              customer workspace summary endpoint live on both servers.
            </div>
          </div>

          {/* Session: April 9, 2026 - Backlog Cleanup, Entitlement Gating, Full Deploy */}
          <div className="border-l-4 border-rose-500 pl-4 mb-8 bg-rose-50/20 py-4 rounded-r-xl">
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-semibold text-gray-900">
                Session: April 9, 2026 — Backlog Cleanup, Entitlement Gating, Full Deploy
              </h4>
              <span className="text-xs text-rose-700 font-medium bg-rose-100 px-2 py-0.5 rounded-full">
                Phase 5 SaaS Monetization
              </span>
            </div>
            <div className="space-y-3 text-sm text-gray-700">
              <div>
                <span className="font-medium text-blue-700">Initial Issue:</span>
                <p className="mt-1">
                  135 uncommitted files on <code>main</code> from multiple prior sessions — SaaS
                  entitlement gating, customer workspace, BHPH customer linking, domain routing
                  refactor, bilingual portal, security hardening, new tests, docs, and tooling all
                  mixed together. The <code>2026-03-22</code> feedback rule said "never commit
                  unstaged blindly" because the last time this pattern happened a destructive
                  hotpatch snuck in and required a full revert.
                </p>
              </div>
              <div>
                <span className="font-medium text-green-700">Key Fixes Applied:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li>
                    Split the backlog into 13 atomic, revertable commits on <code>main</code> (
                    <code>3faa71b</code>..<code>d967bdb</code>), each verified with{' '}
                    <code>git diff</code> before staging.
                  </li>
                  <li>
                    Ran <code>feature-dev:code-reviewer</code> on the entitlement gating before
                    committing. It flagged <strong>2 CRITICAL bugs</strong> that would have 403'd
                    UAB on deploy: <code>stocking_guide</code> router-gated by{' '}
                    <code>ADVANCED_REPORTING</code> (not in basic) and <code>marketplaces</code>{' '}
                    router-gated by <code>ONLINE_MARKETING</code> (not in basic, even though{' '}
                    <code>MARKETPLACE_FACEBOOK</code> IS in basic — outer guard fires first).
                  </li>
                  <li>
                    Fixed both by adding <code>ONLINE_MARKETING</code> and{' '}
                    <code>ADVANCED_REPORTING</code> to the <code>basic</code> plan in{' '}
                    <code>core/entitlements.py</code> with a load-bearing comment explaining the
                    reasoning.
                  </li>
                  <li>
                    Shipped security hardening: real JWT revocation on logout, portal tenant
                    isolation via enforced <code>tenant_id</code>+<code>customer_id</code> UUIDs in
                    the JWT, JWT blacklist fail-closed on Redis errors, OTP fail-closed in
                    production, promise-tracking datetime-TZ bug fixed.
                  </li>
                  <li>
                    Deployed backend + frontend to CarLotPilot and frontend-only to UAB. Built
                    images locally on each server from rsync'd source because git push to{' '}
                    <code>mylimoproject/SimpleDealer</code> is blocked by an SSH auth gap (the
                    workstation's SSH identity is a deploy key for <em>taprideit-enterprise</em>,
                    not a user with SimpleDealer access).
                  </li>
                  <li>
                    Hit and documented 4 new troubleshooting issues: wrong frontend{' '}
                    <code>Dockerfile</code> (dev uses port 3000, prod uses port 80), rsync not
                    installed on UAB, UAB duplicate <code>/opt/SimpleDealer</code> vs{' '}
                    <code>/opt/simpledealer</code> dirs, and the git push auth gap.
                  </li>
                </ul>
              </div>
              <div>
                <span className="font-medium text-purple-700">Validation:</span>
                <p className="mt-1">
                  <code>deployment-verifier</code> agent ran all 6 critical post-deploy checks and
                  returned all PASS. Entitlement fix confirmed live on CarLotPilot:{' '}
                  <code>PLAN_ENTITLEMENTS["basic"]</code> ={' '}
                  <code>
                    ['advanced_reporting', 'inventory_import', 'marketplace_facebook',
                    'online_marketing', 'website_builder']
                  </code>
                  . UAB <code>/</code>, <code>/login</code>, <code>/robots.txt</code>,{' '}
                  <code>/sitemap.xml</code> all 200. Frontend JS bundle hash identical across both
                  servers — dual-frontend parity confirmed. User browser-verified carlotpilot.com
                  and unitedautobrokers.online.
                </p>
              </div>
              <div className="pt-2 border-t border-gray-200">
                <span className="font-medium text-indigo-700">Key Deliverables:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li>
                    <code>core/entitlements.py</code> — <code>EntitlementKey</code> catalog,{' '}
                    <code>PLAN_ENTITLEMENTS</code>, <code>RateLimitProfile</code>, resolution
                    helpers with the critical UAB fix baked in
                  </li>
                  <li>
                    <code>core/security.py</code> + <code>core/jwt_blacklist.py</code> +{' '}
                    <code>api/v1/endpoints/auth.py</code> — real JWT revocation flow, fail-closed
                    blacklist
                  </li>
                  <li>
                    <code>core/customer_security.py</code> +{' '}
                    <code>api/v1/endpoints/customer_portal.py</code> — portal tenant isolation via
                    enforced tenant/customer UUIDs in the JWT
                  </li>
                  <li>
                    <code>api/v1/endpoints/customers.py</code> — new{' '}
                    <code>/customers/&#123;id&#125;/workspace-summary</code> endpoint
                  </li>
                  <li>
                    <code>api/v1/endpoints/bhph_accounts.py</code> —{' '}
                    <code>_get_customer_ids_by_deal_number</code> + unique-name fallback for
                    account↔customer linking
                  </li>
                  <li>
                    <code>main.py</code> + <code>website_builder.py</code> — new tenant-aware{' '}
                    <code>/robots.txt</code> and <code>/sitemap.xml</code> root endpoints
                  </li>
                  <li>
                    <code>services/otp_delivery_service.py</code> — production fail-closed behavior
                  </li>
                  <li>
                    <code>App.tsx</code> + <code>utils/domainRouting.ts</code> +{' '}
                    <code>pages/marketing/CarLotPilotLandingPage.tsx</code> — extracted domain
                    routing utility, replaced inline SaaS placeholder with proper component
                  </li>
                  <li>
                    <code>pages/customers/CustomerWorkspacePage.tsx</code> +{' '}
                    <code>pages/bhph/BHPHAccountsPage.tsx</code> — new surface pages wired through
                    the refactored router
                  </li>
                  <li>
                    <code>pages/customer-portal/CustomerLoginPage.tsx</code> — bilingual (en/es) OTP
                    login flow
                  </li>
                  <li>
                    ~20 new backend + frontend unit tests covering entitlements, JWT blacklist, OTP,
                    customer workspace, BHPH RBAC, and domain routing
                  </li>
                  <li>
                    <code>docs/SESSION_STORYBOOK.md</code> + <code>docs/TROUBLESHOOTING.md</code> +{' '}
                    <code>.claude/tasks/NEXT_SPRINT.md</code> + <code>SuperTrackerPage.tsx</code> —
                    session continuity updated with narrative, 4 new troubleshooting entries,
                    refreshed sprint priorities, and this card
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Session: April 6, 2026 - Website Builder E2E Stabilization */}
          <div className="border-l-4 border-lime-500 pl-4 mb-8 bg-lime-50/20 py-4 rounded-r-xl">
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-semibold text-gray-900">
                Session: April 6, 2026 — Website Builder E2E Stabilization
              </h4>
              <span className="text-xs text-lime-700 font-medium bg-lime-100 px-2 py-0.5 rounded-full">
                Phase 5 E2E
              </span>
            </div>
            <div className="space-y-3 text-sm text-gray-700">
              <div>
                <span className="font-medium text-blue-700">Initial Issue:</span>
                <p className="mt-1">
                  The generic Playwright smoke lane still excluded{' '}
                  <code>website-builder.spec.ts</code>. The save flow looked flaky from the outside,
                  but the real blocker was a deterministic entitlement mismatch: the demo tenant
                  re-posted a stale custom domain and the backend correctly returned{' '}
                  <code>403 Custom Domain is not enabled for this tenant subscription</code>.
                </p>
              </div>
              <div>
                <span className="font-medium text-green-700">Key Fixes Applied:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li>
                    Added explicit label bindings for the Website Builder <code>Headline</code> and{' '}
                    <code>Custom Domain</code> inputs in <code>SettingsPage.tsx</code>.
                  </li>
                  <li>
                    Narrowed <code>src/frontend/tests/website-builder.spec.ts</code> to a
                    basic-plan-safe save flow that clears <code>Custom Domain</code>, updates the
                    headline, saves, reloads, and verifies the persisted headline value.
                  </li>
                  <li>
                    Re-enabled <code>website-builder.spec.ts</code> in the generic Playwright smoke
                    step in <code>.github/workflows/e2e-tests.yml</code>.
                  </li>
                  <li>
                    Logged the failure mode and fix in <code>docs/TROUBLESHOOTING.md</code>.
                  </li>
                </ul>
              </div>
              <div>
                <span className="font-medium text-purple-700">Validation:</span>
                <p className="mt-1">
                  Targeted Playwright validation passed with <code>1 passed</code> for{' '}
                  <code>website-builder.spec.ts</code>. The re-enabled generic smoke lane passed
                  locally with <code>13 passed</code> across <code>profile-page.spec.ts</code> and{' '}
                  <code>website-builder.spec.ts</code>, and frontend <code>lint</code> plus the
                  production <code>build</code> remained green.
                </p>
              </div>
              <div className="pt-2 border-t border-gray-200">
                <span className="font-medium text-indigo-700">Key Deliverables:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li>
                    <code>src/frontend/src/pages/settings/SettingsPage.tsx</code> — semantic label
                    associations for Website Builder headline and custom-domain inputs
                  </li>
                  <li>
                    <code>src/frontend/tests/website-builder.spec.ts</code> — deterministic
                    basic-plan-safe save/reload smoke coverage
                  </li>
                  <li>
                    <code>.github/workflows/e2e-tests.yml</code> — generic Playwright smoke lane now
                    includes the stabilized website-builder spec again
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Session: April 6, 2026 - CarLotPilot SaaS Landing Page */}
          <div className="border-l-4 border-amber-500 pl-4 mb-8 bg-amber-50/20 py-4 rounded-r-xl">
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-semibold text-gray-900">
                Session: April 6, 2026 — CarLotPilot SaaS Landing Page
              </h4>
              <span className="text-xs text-amber-700 font-medium bg-amber-100 px-2 py-0.5 rounded-full">
                Phase 5 Marketing
              </span>
            </div>
            <div className="space-y-3 text-sm text-gray-700">
              <div>
                <span className="font-medium text-blue-700">Initial Issue:</span>
                <p className="mt-1">
                  The CarLotPilot product domain still rendered an inline placeholder inside{' '}
                  <code>App.tsx</code>, and hostname classification logic was duplicated between the
                  root router and home redirect path. That made the product site easy to drift away
                  from the CRM and tenant public-site routing contract.
                </p>
              </div>
              <div>
                <span className="font-medium text-green-700">Key Fixes Applied:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li>
                    Added <code>src/frontend/src/utils/domainRouting.ts</code> so product, app, and
                    public-site hostname classification is shared instead of duplicated.
                  </li>
                  <li>
                    Added <code>src/frontend/src/pages/marketing/CarLotPilotLandingPage.tsx</code>{' '}
                    with Helmet metadata, reused CarLotPilot branding assets, dealer-login CTA, demo
                    CTA, product pillars, and rollout steps.
                  </li>
                  <li>
                    Replaced the inline placeholder in <code>src/frontend/src/App.tsx</code> so{' '}
                    <code>carlotpilot.com</code> and <code>www.carlotpilot.com</code> now render the
                    dedicated marketing page.
                  </li>
                  <li>
                    Added focused frontend regression coverage for hostname classification and
                    landing-page rendering.
                  </li>
                </ul>
              </div>
              <div>
                <span className="font-medium text-purple-700">Validation:</span>
                <p className="mt-1">
                  Focused landing-page Jest validation passed with <code>3 suites / 6 tests</code>{' '}
                  across the new routing helper, the landing page, and the existing preview-routing
                  regression. Follow-up frontend verification cleanup also passed with{' '}
                  <code>3 suites / 7 tests</code>, full frontend <code>lint</code>, and a successful
                  production build after patching <code>DocumentCenterPage.tsx</code>,{' '}
                  <code>PublicSiteShell.previewRouting.test.tsx</code>, and{' '}
                  <code>PaymentProcessingPage.tsx</code>.
                </p>
              </div>
              <div className="pt-2 border-t border-gray-200">
                <span className="font-medium text-indigo-700">Key Deliverables:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li>
                    <code>src/frontend/src/utils/domainRouting.ts</code> +{' '}
                    <code>src/frontend/src/utils/__tests__/domainRouting.test.ts</code> — shared
                    hostname routing contract with focused regression coverage
                  </li>
                  <li>
                    <code>src/frontend/src/pages/marketing/CarLotPilotLandingPage.tsx</code> +{' '}
                    <code>
                      src/frontend/src/pages/marketing/__tests__/CarLotPilotLandingPage.test.tsx
                    </code>{' '}
                    — dedicated product marketing surface plus focused CTA/render coverage
                  </li>
                  <li>
                    <code>src/frontend/src/App.tsx</code> — product-domain routing now resolves
                    through the shared helper instead of an inline placeholder
                  </li>
                  <li>
                    <code>src/frontend/src/pages/documents/DocumentCenterPage.tsx</code> +{' '}
                    <code>
                      src/frontend/src/pages/public/__tests__/PublicSiteShell.previewRouting.test.tsx
                    </code>{' '}
                    + <code>src/frontend/src/pages/bhph/PaymentProcessingPage.tsx</code> — follow-up
                    verification cleanup restored green frontend lint/build
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Session: April 6, 2026 - Auto AI Descriptions on CarsForSale Import */}
          <div className="border-l-4 border-cyan-500 pl-4 mb-8 bg-cyan-50/20 py-4 rounded-r-xl">
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-semibold text-gray-900">
                Session: April 6, 2026 — Auto AI Descriptions on CarsForSale Import
              </h4>
              <span className="text-xs text-cyan-700 font-medium bg-cyan-100 px-2 py-0.5 rounded-full">
                Phase 5 Feature
              </span>
            </div>
            <div className="space-y-3 text-sm text-gray-700">
              <div>
                <span className="font-medium text-blue-700">Initial Issue:</span>
                <p className="mt-1">
                  CarsForSale imports already had the backend pieces to preserve descriptions and
                  identify changed vehicles, but post-import AI regeneration still ran without a
                  tenant-facing default-off control. The queued feature was to expose that control
                  in Settings &gt; Inventory &gt; Import and keep import-triggered AI aligned with
                  the entitlement model.
                </p>
              </div>
              <div>
                <span className="font-medium text-green-700">Key Fixes Applied:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li>
                    Added per-tenant <code>autoGenerateAiDescriptions</code> config to the
                    CarsForSale import contract in{' '}
                    <code>src/backend/api/v1/endpoints/vehicles_import.py</code> with a backend
                    default of off.
                  </li>
                  <li>
                    Routed both <code>/download-and-import</code> and <code>/manual-import</code>{' '}
                    through one shared helper so AI descriptions only regenerate when the toggle is
                    enabled.
                  </li>
                  <li>
                    Required <code>ai_assistant</code> entitlement before import-triggered AI
                    generation can run, preventing import access from bypassing the existing AI
                    entitlement gate.
                  </li>
                  <li>
                    Exposed the toggle in <code>CarsForSaleImportConfig.tsx</code> and updated
                    manual import success feedback to include the AI-generated description count.
                  </li>
                </ul>
              </div>
              <div>
                <span className="font-medium text-purple-700">Validation:</span>
                <p className="mt-1">
                  Focused backend validation passed with <code>9 passed, 20 warnings</code> across
                  the vehicles import entitlement + AI toggle suites. Focused frontend validation
                  passed with <code>3</code> Jest tests for the new toggle, and the final reviewer
                  follow-up reported no blocker/high/medium findings.
                </p>
              </div>
              <div className="pt-2 border-t border-gray-200">
                <span className="font-medium text-indigo-700">Key Deliverables:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li>
                    <code>src/backend/api/v1/endpoints/vehicles_import.py</code> — default-off
                    toggle persistence plus entitlement-aware post-import AI helper for both
                    CarsForSale import entrypoints
                  </li>
                  <li>
                    <code>tests/backend/unit/api/test_vehicles_import_ai_descriptions.py</code> —
                    focused coverage for config persistence and manual/FTP import behavior
                  </li>
                  <li>
                    <code>src/frontend/src/components/vehicles/CarsForSaleImportConfig.tsx</code> +{' '}
                    <code>
                      src/frontend/src/components/vehicles/__tests__/CarsForSaleImportConfig.test.tsx
                    </code>{' '}
                    — Settings UI toggle and focused load/save regression coverage
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Session: April 2, 2026 - Ralph Loop Made Routine */}
          <div className="border-l-4 border-fuchsia-500 pl-4 mb-8 bg-fuchsia-50/20 py-4 rounded-r-xl">
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-semibold text-gray-900">
                Session: April 2, 2026 — Ralph Loop Made Routine
              </h4>
              <span className="text-xs text-fuchsia-700 font-medium bg-fuchsia-100 px-2 py-0.5 rounded-full">
                Workflow Policy
              </span>
            </div>
            <div className="space-y-3 text-sm text-gray-700">
              <div>
                <span className="font-medium text-blue-700">Initial Issue:</span>
                <p className="mt-1">
                  The repo already had a Ralph Loop skill and prompt, but it was still optional in
                  practice. That left room for non-trivial slices to ship without the stronger
                  plan-review-audit gate unless it was invoked manually.
                </p>
              </div>
              <div>
                <span className="font-medium text-green-700">Key Fixes Applied:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li>
                    Updated <code>.github/copilot-instructions.md</code> so Ralph Loop is now the
                    default workflow for non-trivial work instead of an optional extra step.
                  </li>
                  <li>
                    Reinforced the same expectation in <code>.github/OWNER_PREFERENCES.md</code> so
                    workspace instructions and owner execution preferences stay aligned.
                  </li>
                  <li>
                    Kept the rule scoped: trivial text edits and obvious one-file mechanical fixes
                    can still use a lighter path, but behavior changes and cross-layer work now
                    default to the full loop.
                  </li>
                </ul>
              </div>
              <div>
                <span className="font-medium text-purple-700">Validation:</span>
                <p className="mt-1">
                  This slice only touched workflow/customization surfaces and session tracking. The
                  active Phase 5 queue stayed unchanged, and Twilio reminder follow-up remains the
                  next product decision.
                </p>
              </div>
              <div className="pt-2 border-t border-gray-200">
                <span className="font-medium text-indigo-700">Key Deliverables:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li>
                    <code>.github/copilot-instructions.md</code> — always-on Ralph Loop rule for
                    non-trivial slices
                  </li>
                  <li>
                    <code>.github/OWNER_PREFERENCES.md</code> — owner-level reinforcement that
                    non-trivial work must use the full quality loop
                  </li>
                  <li>
                    <code>docs/SESSION_STORYBOOK.md</code> + <code>SuperTrackerPage.tsx</code> —
                    session continuity updated so future sessions inherit the policy change
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Session: April 2, 2026 - Public Website Crawler Metadata + Artifact Cleanup */}
          <div className="border-l-4 border-teal-500 pl-4 mb-8 bg-teal-50/20 py-4 rounded-r-xl">
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-semibold text-gray-900">
                Session: April 2, 2026 — Public Website Crawler Metadata + Artifact Cleanup
              </h4>
              <span className="text-xs text-teal-700 font-medium bg-teal-100 px-2 py-0.5 rounded-full">
                Cleanup + SEO
              </span>
            </div>
            <div className="space-y-3 text-sm text-gray-700">
              <div>
                <span className="font-medium text-blue-700">Initial Issue:</span>
                <p className="mt-1">
                  The remaining review follow-through items were outside the main feature flows: the
                  repo still contained accidental shell/pager dump artifacts, and the public
                  frontend `robots.txt` was hard-coded to the UAB sitemap instead of following the
                  active tenant host.
                </p>
              </div>
              <div>
                <span className="font-medium text-green-700">Key Fixes Applied:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li>
                    Removed accidental root-level shell and pager dump files with no product value.
                  </li>
                  <li>
                    Expanded `.gitignore` to cover `src/frontend/playwright-report/` and removed the
                    generated Playwright HTML artifact from source control.
                  </li>
                  <li>
                    Added tenant-aware `/robots.txt` and `/sitemap.xml` responses in{' '}
                    <code>src/backend/main.py</code> using the existing host-based tenant resolution
                    contract.
                  </li>
                  <li>
                    Added reusable public-site origin, robots, and sitemap helpers in{' '}
                    <code>src/backend/api/v1/endpoints/website_builder.py</code> and routed crawler
                    metadata through <code>Caddyfile</code> before the SPA catch-all.
                  </li>
                  <li>
                    Replaced the frontend fallback `robots.txt` with a domain-agnostic fallback
                    instead of a hard-coded production sitemap URL.
                  </li>
                </ul>
              </div>
              <div>
                <span className="font-medium text-purple-700">Validation:</span>
                <p className="mt-1">
                  Touched-file diagnostics were clean, and focused backend validation passed with{' '}
                  <code>5 passed</code> in{' '}
                  <code>tests/backend/unit/api/test_website_builder_public_settings.py</code>.
                  Remaining warnings were the known repo-wide Pydantic and passlib deprecations.
                </p>
              </div>
              <div className="pt-2 border-t border-gray-200">
                <span className="font-medium text-indigo-700">Key Deliverables:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li>
                    <code>src/backend/main.py</code> +{' '}
                    <code>src/backend/api/v1/endpoints/website_builder.py</code> — crawler metadata
                    is now generated from the active tenant host instead of a static frontend file
                  </li>
                  <li>
                    <code>Caddyfile</code> + <code>src/frontend/public/robots.txt</code> — deployed
                    public sites use backend metadata while static fallback no longer hard-codes the
                    UAB sitemap
                  </li>
                  <li>
                    <code>.gitignore</code> — generated Playwright HTML and stray verification logs
                    no longer dirty the repo
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Session: April 2, 2026 - Public Website Preview Routing Hardening */}
          <div className="border-l-4 border-cyan-500 pl-4 mb-8 bg-cyan-50/20 py-4 rounded-r-xl">
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-semibold text-gray-900">
                Session: April 2, 2026 — Public Website Preview Routing Hardening
              </h4>
              <span className="text-xs text-cyan-700 font-medium bg-cyan-100 px-2 py-0.5 rounded-full">
                Preview Routing
              </span>
            </div>
            <div className="space-y-3 text-sm text-gray-700">
              <div>
                <span className="font-medium text-blue-700">Initial Issue:</span>
                <p className="mt-1">
                  The full workspace re-review found that localhost public-site preview routing was
                  ambiguous: preview pages were mounted on <code>/website/:subdomain?</code>, while
                  public blog and navigation links used root-absolute paths that fell back into the
                  CRM router on localhost.
                </p>
              </div>
              <div>
                <span className="font-medium text-green-700">Key Fixes Applied:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li>
                    Updated <code>App.tsx</code> so bare <code>/website</code> redirects to{' '}
                    <code>/website/preview</code> and preview rendering lives on explicit{' '}
                    <code>/website/:subdomain/*</code> routes.
                  </li>
                  <li>
                    Converted <code>PublicSiteShell.tsx</code> to relative route matching so preview
                    blog, inventory, financing, contact, and customer portal routes resolve under
                    the preview base path.
                  </li>
                  <li>
                    Added <code>publicSitePaths.ts</code> and used it across the public header, blog
                    pages, homepage CTAs, vehicle cards, and vehicle detail links so preview-mode
                    navigation preserves <code>/website/:subdomain</code> while production domains
                    stay unchanged.
                  </li>
                  <li>
                    Added focused frontend regression coverage for preview blog rendering and
                    prefixed navigation links.
                  </li>
                </ul>
              </div>
              <div>
                <span className="font-medium text-purple-700">Validation:</span>
                <p className="mt-1">
                  Touched-file diagnostics were clean, and focused frontend validation passed with{' '}
                  <code>1</code> suite / <code>2</code> tests. Remaining console noise came from the
                  upstream React/RTL <code>act</code> deprecation warning already present elsewhere
                  in the repo.
                </p>
              </div>
              <div className="pt-2 border-t border-gray-200">
                <span className="font-medium text-indigo-700">Key Deliverables:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li>
                    <code>src/frontend/src/App.tsx</code> +{' '}
                    <code>src/frontend/src/pages/public/PublicSiteShell.tsx</code> — preview routing
                    is now explicit and resolves descendant public routes under localhost preview
                    mode
                  </li>
                  <li>
                    <code>src/frontend/src/pages/public/publicSitePaths.ts</code> — shared
                    preview-aware public link builder for header, blog, inventory, and CTA
                    navigation
                  </li>
                  <li>
                    <code>
                      src/frontend/src/pages/public/__tests__/PublicSiteShell.previewRouting.test.tsx
                    </code>{' '}
                    — focused regression coverage for preview blog rendering and prefixed public
                    links
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Session: April 2, 2026 - Phase 5 Navigation Contract Hardening */}
          <div className="border-l-4 border-blue-500 pl-4 mb-8 bg-blue-50/20 py-4 rounded-r-xl">
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-semibold text-gray-900">
                Session: April 2, 2026 — Phase 5 Navigation Contract Hardening
              </h4>
              <span className="text-xs text-blue-700 font-medium bg-blue-100 px-2 py-0.5 rounded-full">
                Workflow Fixes
              </span>
            </div>
            <div className="space-y-3 text-sm text-gray-700">
              <div>
                <span className="font-medium text-blue-700">Initial Issue:</span>
                <p className="mt-1">
                  A full workspace re-review found that several navigation paths still broke user
                  context: the BHPH accounts Pay action dropped account selection,
                  `PaymentProcessingPage` only honored the first account query param it saw, and
                  customer workspace links into deals and documents dropped `customer_id` context.
                </p>
              </div>
              <div>
                <span className="font-medium text-green-700">Key Fixes Applied:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li>
                    Updated <code>BHPHAccountsPage.tsx</code> so row-level Pay actions route to{' '}
                    <code>/bhph/payment?account=&lt;account-id&gt;</code>.
                  </li>
                  <li>
                    Added query-param re-resolution logic in <code>PaymentProcessingPage.tsx</code>{' '}
                    with a dedicated helper so later account changes are processed instead of
                    ignored.
                  </li>
                  <li>
                    Updated <code>DealsPage.tsx</code> to pass <code>customer_id</code> to the
                    canonical backend deals list endpoint.
                  </li>
                  <li>
                    Updated <code>DocumentCenterPage.tsx</code> to load saved customer documents
                    from <code>/documents/customers/&lt;customer_id&gt;</code> while preserving the
                    template-generation workflow.
                  </li>
                  <li>
                    Added focused frontend regression coverage for BHPH payment navigation and
                    customer-scoped deals/documents loading.
                  </li>
                </ul>
              </div>
              <div>
                <span className="font-medium text-purple-700">Validation:</span>
                <p className="mt-1">
                  Touched-file diagnostics were clean, and focused frontend validation passed with{' '}
                  <code>4</code> suites / <code>7</code> tests. Remaining console noise came from
                  upstream React/RTL act deprecation warnings rather than failing assertions.
                </p>
              </div>
              <div className="pt-2 border-t border-gray-200">
                <span className="font-medium text-indigo-700">Key Deliverables:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li>
                    <code>src/frontend/src/pages/bhph/BHPHAccountsPage.tsx</code> +{' '}
                    <code>src/frontend/src/pages/bhph/PaymentProcessingPage.tsx</code> — BHPH
                    payment routing now preserves and reacts to selected account context
                  </li>
                  <li>
                    <code>src/frontend/src/pages/deals/DealsPage.tsx</code> +{' '}
                    <code>src/frontend/src/pages/documents/DocumentCenterPage.tsx</code> — customer
                    workspace links now retain customer scope through destination pages
                  </li>
                  <li>
                    <code>src/frontend/src/pages/*/__tests__/*.test.tsx</code> — focused frontend
                    regression coverage added for the repaired navigation contracts
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Session: April 2, 2026 - Phase 5 Reviewer Validation + Seeded BHPH Hardening */}
          <div className="border-l-4 border-emerald-500 pl-4 mb-8 bg-emerald-50/20 py-4 rounded-r-xl">
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-semibold text-gray-900">
                Session: April 2, 2026 — Phase 5 Reviewer Validation + Seeded BHPH Hardening
              </h4>
              <span className="text-xs text-emerald-700 font-medium bg-emerald-100 px-2 py-0.5 rounded-full">
                Validation Complete
              </span>
            </div>
            <div className="space-y-3 text-sm text-gray-700">
              <div>
                <span className="font-medium text-blue-700">Initial Issue:</span>
                <p className="mt-1">
                  The requested independent review pass for the completed Phase 5 hardening lane
                  surfaced a final set of acceptance gaps around BHPH seeding, vacuous Playwright
                  success paths, and JWT revocation behavior under Redis failure.
                </p>
              </div>
              <div>
                <span className="font-medium text-green-700">Key Fixes Applied:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li>
                    Reworked <code>seed_dealership_data.py</code> to use repo-relative data paths,
                    seed a deterministic demo location, and import active delinquent BHPH accounts
                    for CI worklist coverage.
                  </li>
                  <li>
                    Hardened <code>bhph-workflows.spec.ts</code> so seeded worklist, account-detail,
                    and payment flows fail when seeded rows are missing instead of silently passing
                    on empty-state paths.
                  </li>
                  <li>
                    Changed <code>jwt_blacklist.py</code> to fail closed on Redis lookup errors and
                    fixed blacklist scan termination for integer Redis cursors.
                  </li>
                  <li>
                    Added focused regression coverage in{' '}
                    <code>tests/backend/unit/core/test_jwt_blacklist.py</code> for Redis-error
                    fail-closed behavior and integer cursor handling.
                  </li>
                </ul>
              </div>
              <div>
                <span className="font-medium text-purple-700">Validation:</span>
                <p className="mt-1">
                  Touched-file diagnostics were clean, backend compile checks passed for the updated
                  seed and blacklist modules, focused backend validation passed with{' '}
                  <code>4 passed, 20 warnings</code>, and the final independent reviewer pass
                  reported no remaining findings in this lane.
                </p>
              </div>
              <div className="pt-2 border-t border-gray-200">
                <span className="font-medium text-indigo-700">Key Deliverables:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li>
                    <code>src/backend/scripts/seed_dealership_data.py</code> — CI seeding is now
                    portable and guarantees seeded delinquent BHPH worklist rows
                  </li>
                  <li>
                    <code>src/frontend/tests/bhph-workflows.spec.ts</code> — seeded BHPH Playwright
                    coverage now rejects empty-state false positives
                  </li>
                  <li>
                    <code>src/backend/core/jwt_blacklist.py</code> +{' '}
                    <code>tests/backend/unit/core/test_jwt_blacklist.py</code> — JWT revocation now
                    fails closed on Redis errors and handles real Redis cursor shapes
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Session: April 2, 2026 - Phase 5 Frontend CI + E2E + Operational Verification */}
          <div className="border-l-4 border-sky-500 pl-4 mb-8 bg-sky-50/20 py-4 rounded-r-xl">
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-semibold text-gray-900">
                Session: April 2, 2026 — Phase 5 Frontend CI + E2E + Operational Verification
              </h4>
              <span className="text-xs text-sky-700 font-medium bg-sky-100 px-2 py-0.5 rounded-full">
                Infra Hardening
              </span>
            </div>
            <div className="space-y-3 text-sm text-gray-700">
              <div>
                <span className="font-medium text-blue-700">Initial Issue:</span>
                <p className="mt-1">
                  After backend CI was repaired, the remaining Phase 5 hardening gap was
                  concentrated in frontend CI drift, stale Playwright workflow assumptions, and
                  unproven operational jobs around late fees, OTP delivery, and JWT revocation.
                </p>
              </div>
              <div>
                <span className="font-medium text-green-700">Key Fixes Applied:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li>
                    Realigned <code>.github/workflows/frontend-ci.yml</code> to the CRA toolchain
                    with a real <code>test:coverage</code> script, CRA <code>build/</code>{' '}
                    artifacts, and the correct coverage upload path.
                  </li>
                  <li>
                    Converted the BHPH API service test suite from Vitest-style assumptions to
                    CRA/Jest and fixed timezone-safe date formatting in{' '}
                    <code>src/frontend/src/services/bhphApi.ts</code>.
                  </li>
                  <li>
                    Reworked <code>.github/workflows/e2e-tests.yml</code> to the real startup
                    contract: <code>uvicorn main:app</code>, CRA/Playwright on port{' '}
                    <code>3000</code>, deterministic admin seeding, and a dedicated seeded BHPH job.
                  </li>
                  <li>
                    Restored the missing <code>daily-late-fees</code> Celery beat entry and added
                    focused backend coverage for late-fee scheduling, logout revocation, and JWT
                    blacklist behavior.
                  </li>
                  <li>
                    Normalized the OTP Twilio client import path so production-path tests remain
                    fail-closed and patchable.
                  </li>
                </ul>
              </div>
              <div>
                <span className="font-medium text-purple-700">Validation:</span>
                <p className="mt-1">
                  Frontend local validation passed with lint clean, focused CRA/Jest coverage{' '}
                  <code>36 passed</code>, and a successful production build. Backend focused
                  verification passed with <code>45 passed, 20 warnings</code>. Local Playwright
                  validation passed for <code>profile-page.spec.ts</code> (<code>12 passed</code>)
                  plus a representative seeded BHPH dashboard smoke (<code>1 passed</code>).
                </p>
              </div>
              <div>
                <span className="font-medium text-orange-700">Residual Risk:</span>
                <p className="mt-1">
                  <code>website-builder.spec.ts</code> is still flaky under the seeded settings
                  state, so the generic smoke workflow intentionally excludes it until the
                  website-settings save flow is stabilized.
                </p>
              </div>
              <div className="pt-2 border-t border-gray-200">
                <span className="font-medium text-indigo-700">Key Deliverables:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li>
                    <code>.github/workflows/frontend-ci.yml</code> +{' '}
                    <code>.github/workflows/e2e-tests.yml</code> — CI contracts now match the real
                    frontend and Playwright topology
                  </li>
                  <li>
                    <code>src/frontend/src/services/__tests__/bhphApi.test.ts</code> +{' '}
                    <code>src/frontend/src/services/bhphApi.ts</code> — Jest compatibility restored
                    and date-only display logic fixed
                  </li>
                  <li>
                    <code>src/backend/core/celery_app.py</code> + focused backend tests — late-fee
                    schedule and JWT revocation verification are explicit
                  </li>
                  <li>
                    <code>docs/SESSION_STORYBOOK.md</code> + tracker docs — Phase 5 hardening state
                    advanced beyond CI/E2E/operational verification
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Session: April 2, 2026 - Phase 5 Backend CI Realignment */}
          <div className="border-l-4 border-violet-500 pl-4 mb-8 bg-violet-50/20 py-4 rounded-r-xl">
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-semibold text-gray-900">
                Session: April 2, 2026 — Phase 5 Backend CI Realignment
              </h4>
              <span className="text-xs text-violet-700 font-medium bg-violet-100 px-2 py-0.5 rounded-full">
                Infra Hardening
              </span>
            </div>
            <div className="space-y-3 text-sm text-gray-700">
              <div>
                <span className="font-medium text-blue-700">Initial Issue:</span>
                <p className="mt-1">
                  The fresh audit showed that backend CI still targeted an obsolete{' '}
                  <code>app/</code> layout and did not reflect the real backend import, lint,
                  typing, and test surface.
                </p>
              </div>
              <div>
                <span className="font-medium text-green-700">Key Fixes Applied:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li>
                    Realigned <code>.github/workflows/backend-ci.yml</code> to the real{' '}
                    <code>src/backend</code> source layout and repo-root pytest bootstrap.
                  </li>
                  <li>
                    Added explicit backend <code>core</code> and <code>services</code> packages to
                    avoid pytest import collisions.
                  </li>
                  <li>
                    Removed the shadowing test package marker at{' '}
                    <code>tests/backend/unit/services/__init__.py</code>.
                  </li>
                  <li>
                    Fixed backend marketplace endpoint defects surfaced by the fatal lint gate:
                    missing <code>PLATFORM_REGISTRY</code> import and missing async <code>db</code>{' '}
                    dependency.
                  </li>
                  <li>
                    Scoped the backend CI type gate to release-critical contract modules while
                    broader type debt remains tracked separately.
                  </li>
                </ul>
              </div>
              <div>
                <span className="font-medium text-purple-700">Validation:</span>
                <p className="mt-1">
                  Local verification passed with fatal flake8 <code>0</code>, scoped mypy green, and
                  focused backend pytest <code>23 passed, 20 warnings</code>.
                </p>
              </div>
              <div className="pt-2 border-t border-gray-200">
                <span className="font-medium text-indigo-700">Key Deliverables:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li>
                    <code>.github/workflows/backend-ci.yml</code> — backend CI now matches the real
                    repository layout and test bootstrap
                  </li>
                  <li>
                    <code>src/backend/core/__init__.py</code> +{' '}
                    <code>src/backend/services/__init__.py</code> — explicit backend packages for
                    stable imports
                  </li>
                  <li>
                    <code>src/backend/api/v1/endpoints/marketplaces.py</code> +{' '}
                    <code>src/backend/services/support_grant_service.py</code> — defects fixed
                    during CI hardening
                  </li>
                  <li>
                    <code>TODO.md</code> + <code>tasks/todo.md</code> +{' '}
                    <code>docs/SESSION_STORYBOOK.md</code> — tracker state advanced so frontend CI
                    is the next lane
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Session: April 1, 2026 - Phase 5 Frontend BHPH Route Prefix Audit Complete */}
          <div className="border-l-4 border-cyan-500 pl-4 mb-8 bg-cyan-50/20 py-4 rounded-r-xl">
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-semibold text-gray-900">
                Session: April 1, 2026 — Phase 5 Frontend BHPH Route Prefix Audit Complete
              </h4>
              <span className="text-xs text-cyan-700 font-medium bg-cyan-100 px-2 py-0.5 rounded-full">
                Phase 5 Complete
              </span>
            </div>
            <div className="space-y-3 text-sm text-gray-700">
              <div>
                <span className="font-medium text-blue-700">Initial Issue:</span>
                <p className="mt-1">
                  The current Phase 5 item needed to be closed out with the real frontend regression
                  and the correct next active item reflected across the session trackers.
                </p>
              </div>
              <div>
                <span className="font-medium text-green-700">Key Fixes Applied:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li>
                    Confirmed the main router contract was already aligned, so the audit did not
                    require a router-level contract change.
                  </li>
                  <li>
                    Captured the concrete frontend regression:{' '}
                    <code>/bhph/payment?account=...</code> deep links from BHPH pages were ignored
                    by <code>PaymentProcessingPage</code>.
                  </li>
                  <li>
                    Recorded that <code>PaymentProcessingPage</code> now reads the{' '}
                    <code>account</code> query param, loads UUID account links directly, and
                    pre-fills non-UUID search values.
                  </li>
                  <li>
                    Advanced the canonical Phase 5 trackers so Operational verification batch is now
                    the next active item.
                  </li>
                </ul>
              </div>
              <div>
                <span className="font-medium text-purple-700">Validation:</span>
                <p className="mt-1">
                  Focused frontend validation passed: <code>src/utils/__tests__/uuid.test.ts</code>{' '}
                  → <code>1 suite passed, 3 tests passed</code>.
                </p>
              </div>
              <div className="pt-2 border-t border-gray-200">
                <span className="font-medium text-indigo-700">Key Deliverables:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li>
                    <code>TODO.md</code> + <code>tasks/todo.md</code> +{' '}
                    <code>.claude/tasks/NEXT_SPRINT.md</code> — Frontend BHPH route prefix audit
                    marked complete and next active item advanced
                  </li>
                  <li>
                    <code>docs/SESSION_STORYBOOK.md</code> + <code>SuperTrackerPage.tsx</code> —
                    April 1, 2026 session continuity updated with audit findings and validation
                    result
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Session: April 1, 2026 - Phase 5 Inventory General Settings Slice Complete */}
          <div className="border-l-4 border-emerald-500 pl-4 mb-8 bg-emerald-50/20 py-4 rounded-r-xl">
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-semibold text-gray-900">
                Session: April 1, 2026 — Phase 5 Inventory General Settings Slice Complete
              </h4>
              <span className="text-xs text-emerald-700 font-medium bg-emerald-100 px-2 py-0.5 rounded-full">
                Phase 5 Complete
              </span>
            </div>
            <div className="space-y-3 text-sm text-gray-700">
              <div>
                <span className="font-medium text-blue-700">Initial Issue:</span>
                <p className="mt-1">
                  Inventory General settings needed to be recorded as complete across the canonical
                  sprint trackers once the slice was tenant-backed end-to-end and no longer depended
                  on localStorage as the authoritative source.
                </p>
              </div>
              <div>
                <span className="font-medium text-green-700">Key Fixes Applied:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li>Inventory General settings are now tenant-backed end-to-end.</li>
                  <li>
                    Backend contract exists at <code>GET/POST /settings/inventory</code> backed by{' '}
                    <code>tenant.settings['inventory']</code>.
                  </li>
                  <li>
                    Frontend Inventory General in <code>SettingsPage</code> now loads and saves
                    through the API instead of treating <code>localStorage</code> as authoritative.
                  </li>
                  <li>
                    Canonical Phase 5 trackers were advanced so Frontend BHPH route prefix audit is
                    now the next active item.
                  </li>
                </ul>
              </div>
              <div>
                <span className="font-medium text-purple-700">Validation:</span>
                <p className="mt-1">
                  Focused backend pytest result:{' '}
                  <code>tests/backend/unit/api/test_settings_inventory.py</code> →{' '}
                  <code>8 passed, 20 warnings</code>.
                </p>
              </div>
              <div className="pt-2 border-t border-gray-200">
                <span className="font-medium text-indigo-700">Key Deliverables:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li>
                    <code>src/backend/api/v1/endpoints/settings.py</code> — tenant-backed{' '}
                    <code>GET/POST /settings/inventory</code> contract backed by tenant settings
                  </li>
                  <li>
                    <code>src/frontend/src/pages/settings/SettingsPage.tsx</code> — Inventory
                    General now uses the API as the source of truth
                  </li>
                  <li>
                    <code>tests/backend/unit/api/test_settings_inventory.py</code> — focused
                    contract validation passing with <code>8 passed, 20 warnings</code>
                  </li>
                  <li>
                    <code>TODO.md</code> + <code>tasks/todo.md</code> +{' '}
                    <code>.claude/tasks/NEXT_SPRINT.md</code> +{' '}
                    <code>docs/SESSION_STORYBOOK.md</code> — tracker state advanced to the next
                    Phase 5 item
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Session: April 1, 2026 - Phase 5 Tracker Normalization + Deals Salesperson RBAC Verification */}
          <div className="border-l-4 border-sky-500 pl-4 mb-8 bg-sky-50/20 py-4 rounded-r-xl">
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-semibold text-gray-900">
                Session: April 1, 2026 — Phase 5 Tracker Normalization + Deals Salesperson RBAC
                Verification
              </h4>
              <span className="text-xs text-sky-700 font-medium bg-sky-100 px-2 py-0.5 rounded-full">
                Phase 5 Update
              </span>
            </div>
            <div className="space-y-3 text-sm text-gray-700">
              <div>
                <span className="font-medium text-blue-700">Initial Issue:</span>
                <p className="mt-1">
                  The first Phase 5 item needed explicit verification: confirm whether deals
                  salesperson RBAC required a backend fix or only targeted regression coverage.
                </p>
              </div>
              <div>
                <span className="font-medium text-green-700">Key Fixes Applied:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li>
                    Added focused backend coverage in{' '}
                    <code>tests/backend/unit/api/test_deals_salesperson_rbac.py</code>.
                  </li>
                  <li>
                    Verified current deals salesperson RBAC behavior was already correct, so no
                    backend code fix was required.
                  </li>
                  <li>
                    Normalized Phase 5 tracking docs so Inventory General Settings backend is now
                    the next active item.
                  </li>
                </ul>
              </div>
              <div>
                <span className="font-medium text-purple-700">Validation:</span>
                <p className="mt-1">
                  Focused pytest result: <code>9 passed, 20 warnings</code>.
                </p>
              </div>
              <div className="pt-2 border-t border-gray-200">
                <span className="font-medium text-indigo-700">Key Deliverables:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li>
                    <code>tests/backend/unit/api/test_deals_salesperson_rbac.py</code> — explicit
                    deals salesperson RBAC verification coverage
                  </li>
                  <li>
                    <code>tasks/todo.md</code> + <code>.claude/tasks/NEXT_SPRINT.md</code> +{' '}
                    <code>TODO.md</code> — Phase 5 tracker state aligned
                  </li>
                  <li>
                    <code>docs/SESSION_STORYBOOK.md</code> + <code>SuperTrackerPage.tsx</code> —
                    April 1, 2026 session continuity entry added
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Session: March 30, 2026 - Phase 4 Test & Release Hardening */}
          <div className="border-l-4 border-emerald-500 pl-4 mb-8 bg-emerald-50/20 py-4 rounded-r-xl">
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-semibold text-gray-900">
                Session: March 30, 2026 — Phase 4: Test &amp; Release Hardening
              </h4>
              <span className="text-xs text-emerald-700 font-medium bg-emerald-100 px-2 py-0.5 rounded-full">
                Phase 4 Complete
              </span>
            </div>
            <div className="space-y-3 text-sm text-gray-700">
              <div>
                <span className="font-medium text-blue-700">Initial Issue:</span>
                <p className="mt-1">
                  Full-suite verification was not deterministic. The active pytest surface included
                  manual live-server scripts and unsupported `TestClient` tests, and one BHPH
                  promise scanner path failed with naive-vs-aware datetime comparison.
                </p>
              </div>
              <div>
                <span className="font-medium text-green-700">Key Fixes Applied:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li>
                    Added root <code>conftest.py</code> to normalize <code>src/backend</code> import
                    bootstrap and exclude manual scripts from collection:{' '}
                    <code>tests/verify_contracting_api.py</code>,{' '}
                    <code>tests/backend/final_image_test.py</code>,{' '}
                    <code>tests/unit/test_api.py</code>.
                  </li>
                  <li>
                    Added temporary exclusion for{' '}
                    <code>tests/unit/test_enhanced_deal_calculator.py</code> because{' '}
                    <code>starlette 0.36.3 + httpx 0.28</code> no longer supports{' '}
                    <code>TestClient(app)</code>.
                  </li>
                  <li>
                    Created <code>tests/unit/conftest.py</code> with canonical async fixtures using{' '}
                    <code>httpx.AsyncClient + ASGITransport</code>, auth headers, and{' '}
                    <code>get_current_user</code> override.
                  </li>
                  <li>
                    Fixed <code>scan_and_mark_broken_promises</code> in{' '}
                    <code>promise_tracking_service.py</code> by normalizing naive SQLite datetimes
                    to UTC before cutoff comparison.
                  </li>
                  <li>
                    Session-close parallel audits (security + frontend) completed green after wiring
                    JWT revocation blacklist checks into runtime decode/logout and tightening
                    customer-portal token claim + tenant validation.
                  </li>
                </ul>
              </div>
              <div>
                <span className="font-medium text-purple-700">Validation:</span>
                <p className="mt-1">
                  Full test suite now passes cleanly:{' '}
                  <code>157 passed, 2 skipped, 0 failed, 0 errors</code>.
                </p>
              </div>
              <div className="pt-2 border-t border-gray-200">
                <span className="font-medium text-indigo-700">Key Deliverables:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li>
                    <code>conftest.py</code> — root pytest bootstrap + collection hardening
                  </li>
                  <li>
                    <code>tests/unit/conftest.py</code> — canonical async unit fixtures
                  </li>
                  <li>
                    <code>src/backend/services/promise_tracking_service.py</code> — timezone-safe
                    promise scan comparison
                  </li>
                  <li>
                    <code>src/backend/core/security.py</code> +{' '}
                    <code>src/backend/api/v1/endpoints/auth.py</code> — JWT blacklist revocation now
                    enforced in decode/logout flow
                  </li>
                  <li>
                    <code>src/backend/core/customer_security.py</code> +{' '}
                    <code>src/backend/api/v1/endpoints/customer_portal.py</code> — portal token
                    tenant/claim validation hardened
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Session: March 30, 2026 - Phase 3 Environment & Deployment Contract Stabilization */}
          <div className="border-l-4 border-orange-500 pl-4 mb-8 bg-orange-50/20 py-4 rounded-r-xl">
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-semibold text-gray-900">
                Session: March 30, 2026 — Phase 3: Environment &amp; Deployment Contract
                Stabilization
              </h4>
              <span className="text-xs text-orange-700 font-medium bg-orange-100 px-2 py-0.5 rounded-full">
                Phase 3 Complete
              </span>
            </div>
            <div className="space-y-3 text-sm text-gray-700">
              <div>
                <span className="font-medium text-blue-700">Pre-Phase Health Check:</span>
                <p className="mt-1">
                  116/116 backend tests passing. Backend health endpoint:{' '}
                  <code>status=healthy, database=connected, cache=connected</code>. Frontend
                  TypeScript: 0 production errors.
                </p>
              </div>
              <div>
                <span className="font-medium text-green-700">Issues Fixed:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li>
                    <code>scripts/deploy.sh</code> — <code>DEPLOY_DIR</code> was{' '}
                    <code>/opt/simpledealer</code> (breaking); corrected to{' '}
                    <code>/opt/SimpleDealer</code> to match actual server path.
                  </li>
                  <li>
                    <code>docker-compose.yml</code> — <code>POSTGRES_DB</code> and{' '}
                    <code>DATABASE_URL</code> default changed from <code>simpledealer_prod</code> →{' '}
                    <code>simpledealer_dev</code> (dev compose was silently using prod DB name).
                  </li>
                  <li>
                    Root <code>.env.example</code> — <code>POSTGRES_DB</code> to{' '}
                    <code>simpledealer_dev</code>; hardcoded absolute local path{' '}
                    <code>DATA_DIR=/home/jose-a-pichardo/...</code> replaced with relative{' '}
                    <code>./data/imported-data</code>.
                  </li>
                  <li>
                    <code>src/frontend/.env.example</code> — Real demo password{' '}
                    <code>Admin$ecure!2026</code> removed from version-controlled template (security
                    hygiene).
                  </li>
                  <li>
                    <code>docs/deployment/DEPLOYMENT_GUIDE_DEV_SERVER.md</code> — Server path
                    corrected from <code>/opt/simpledealer</code> to <code>/opt/SimpleDealer</code>.
                  </li>
                </ul>
              </div>
              <div className="pt-2 border-t border-gray-200">
                <span className="font-medium text-indigo-700">Key Deliverables:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li>
                    <code>scripts/deploy.sh</code> — Deploy directory path corrected
                  </li>
                  <li>
                    <code>docker-compose.yml</code> — Dev DB name aligned
                  </li>
                  <li>
                    <code>.env.example</code> — Path and DB name cleaned
                  </li>
                  <li>
                    <code>src/frontend/.env.example</code> — Demo password placeholder only
                  </li>
                  <li>
                    <code>docs/deployment/DEPLOYMENT_GUIDE_DEV_SERVER.md</code> — Path corrected
                  </li>
                </ul>
              </div>
              <div>
                <span className="font-medium text-orange-700">Next Steps:</span>
                <p className="mt-1">
                  Phase 4 — Test and Release Hardening: keep only supported smoke/regression paths,
                  normalize auth/bootstrap expectations, align release-readiness claims with real
                  checks.
                </p>
              </div>
            </div>
          </div>

          {/* Session: March 30, 2026 - First-Class Entitlement State Contract + Compatibility Tests */}
          <div className="border-l-4 border-teal-500 pl-4 mb-8 bg-teal-50/20 py-4 rounded-r-xl">
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-semibold text-gray-900">
                Session: March 30, 2026 - First-Class Entitlement State Contract + Compatibility
                Tests
              </h4>
              <span className="text-xs text-teal-700 font-medium bg-teal-100 px-2 py-0.5 rounded-full">
                Completed
              </span>
            </div>
            <div className="space-y-3 text-sm text-gray-700">
              <div>
                <span className="font-medium text-blue-700">Initial Issue:</span>
                <p className="mt-1">
                  Phase 2 still had one design gap: entitlement checks were spread across helper
                  functions without a single typed state contract that cleanly separates tenant
                  capability modeling from staff-role authorization.
                </p>
              </div>
              <div>
                <span className="font-medium text-green-700">Key Fixes Applied:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li>
                    Added typed entitlement contract objects <code>SubscriptionPlanDefinition</code>{' '}
                    and <code>TenantEntitlementState</code> in{' '}
                    <code>src/backend/core/entitlements.py</code>.
                  </li>
                  <li>
                    Introduced <code>resolve_tenant_entitlement_state(...)</code> as the canonical
                    resolver and routed entitlement and rate-limit helper paths through it.
                  </li>
                  <li>
                    Added <code>Tenant.entitlement_state()</code> in{' '}
                    <code>src/backend/models/tenant.py</code> and routed{' '}
                    <code>effective_entitlements()</code> through typed state.
                  </li>
                  <li>
                    Kept backward compatibility for existing tenant settings keys while adding
                    focused contract tests for precedence and malformed-fallback behavior.
                  </li>
                </ul>
              </div>
              <div>
                <span className="font-medium text-purple-700">Validation:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li>
                    Focused backend suite passed: <code>19 passed</code>.
                  </li>
                  <li>
                    No regressions in existing AI/settings, Website Builder custom-domain, Vehicle
                    Import, or rate-limit gate tests.
                  </li>
                </ul>
              </div>
              <div className="pt-2 border-t border-gray-200">
                <span className="font-medium text-indigo-700">Key Deliverables:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li>
                    <code>src/backend/core/entitlements.py</code> - typed entitlement-state contract
                    and canonical resolver
                  </li>
                  <li>
                    <code>src/backend/models/tenant.py</code> - tenant-level entitlement-state
                    accessor
                  </li>
                  <li>
                    <code>tests/backend/unit/core/test_entitlements.py</code> - compatibility and
                    precedence contract coverage
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Session: March 30, 2026 - Entitlement-Based Rate-Limit Profile Refactor */}
          <div className="border-l-4 border-sky-500 pl-4 mb-8 bg-sky-50/20 py-4 rounded-r-xl">
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-semibold text-gray-900">
                Session: March 30, 2026 - Entitlement-Based Rate-Limit Profile Refactor
              </h4>
              <span className="text-xs text-sky-700 font-medium bg-sky-100 px-2 py-0.5 rounded-full">
                Completed
              </span>
            </div>
            <div className="space-y-3 text-sm text-gray-700">
              <div>
                <span className="font-medium text-blue-700">Initial Issue:</span>
                <p className="mt-1">
                  The global rate limiter still used direct plan-name lookups in runtime policy
                  evaluation, which left a Phase 2 dependency unresolved after ownership mapping
                  completion.
                </p>
              </div>
              <div>
                <span className="font-medium text-green-700">Key Fixes Applied:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li>
                    Added an explicit entitlement-profile contract in{' '}
                    <code>src/backend/core/entitlements.py</code> with a canonical resolver{' '}
                    <code>get_rate_limit_profile(...)</code>.
                  </li>
                  <li>
                    Updated <code>src/backend/core/rate_limit.py</code> so default limiter policy
                    resolves from entitlement profile sources instead of direct plan normalization.
                  </li>
                  <li>
                    Preserved backward compatibility via legacy{' '}
                    <code>settings.rate_limit_profile</code> while preferring{' '}
                    <code>settings.entitlement_profiles.rate_limit</code>.
                  </li>
                  <li>
                    Expanded focused unit coverage in{' '}
                    <code>tests/backend/unit/core/test_rate_limit.py</code> for profile override
                    precedence and invalid-profile fallback behavior.
                  </li>
                </ul>
              </div>
              <div>
                <span className="font-medium text-purple-700">Validation:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li>
                    Focused backend suite passed: <code>14 passed</code>.
                  </li>
                  <li>
                    No regressions in existing entitlement guard tests for settings, website builder
                    custom domain, or vehicle import.
                  </li>
                </ul>
              </div>
              <div className="pt-2 border-t border-gray-200">
                <span className="font-medium text-indigo-700">Key Deliverables:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li>
                    <code>src/backend/core/entitlements.py</code> - added rate-limit profile model
                    and resolver
                  </li>
                  <li>
                    <code>src/backend/core/rate_limit.py</code> - runtime profile-based limit
                    resolution
                  </li>
                  <li>
                    <code>tests/backend/unit/core/test_rate_limit.py</code> - expanded profile
                    override and fallback coverage
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Session: March 30, 2026 - Phase 2 Ownership Mapping Completion + Sequential Execution Rule Sync */}
          <div className="border-l-4 border-violet-500 pl-4 mb-8 bg-violet-50/20 py-4 rounded-r-xl">
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-semibold text-gray-900">
                Session: March 30, 2026 - Phase 2 Ownership Mapping Completion + Sequential
                Execution Rule Sync
              </h4>
              <span className="text-xs text-violet-700 font-medium bg-violet-100 px-2 py-0.5 rounded-full">
                Completed
              </span>
            </div>
            <div className="space-y-3 text-sm text-gray-700">
              <div>
                <span className="font-medium text-blue-700">Initial Issue:</span>
                <p className="mt-1">
                  Phase 2 runtime gates had advanced, but the architecture lane still lacked
                  complete ownership mapping for the mounted backend endpoint families. The user
                  also clarified that every request to work "Sequentially" means dependency-first
                  and root-cause-first execution.
                </p>
              </div>
              <div>
                <span className="font-medium text-green-700">Key Fixes Applied:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li>
                    Recorded the persistent preference that downstream work must not start before
                    prerequisite ownership/root-cause work is complete.
                  </li>
                  <li>
                    Audited the mounted router list in <code>src/backend/api/v1/api.py</code> and
                    used that as the source of truth for Phase 2 mapping coverage.
                  </li>
                  <li>
                    Expanded <code>docs/SAAS_TENANT_BOUNDARY.md</code> to cover inventory/customer
                    operations, deal servicing, documents/credit intake, team/audit/notifications,
                    GPS, and customer portal surfaces.
                  </li>
                  <li>
                    Updated <code>tasks/todo.md</code>, <code>.claude/tasks/NEXT_SPRINT.md</code>,
                    and <code>TODO.md</code> so Phase 2 feature ownership mapping is now explicitly
                    complete for the mounted v1 API surface.
                  </li>
                </ul>
              </div>
              <div>
                <span className="font-medium text-purple-700">Validation:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li>
                    The ownership map now matches the endpoint families actually mounted by{' '}
                    <code>src/backend/api/v1/api.py</code>.
                  </li>
                  <li>
                    No runtime code changed in this slice, so no backend behavior shifted while
                    closing the documentation prerequisite.
                  </li>
                </ul>
              </div>
              <div className="pt-2 border-t border-gray-200">
                <span className="font-medium text-indigo-700">Key Deliverables:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li>
                    <code>docs/SAAS_TENANT_BOUNDARY.md</code> - completed mounted-router
                    control-domain mapping
                  </li>
                  <li>
                    <code>tasks/todo.md</code> - Phase 2 feature ownership mapping marked complete
                  </li>
                  <li>
                    <code>.claude/tasks/NEXT_SPRINT.md</code> - active lane state synchronized with
                    completed mapping prerequisite
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Session: March 28, 2026 - BHPH Accounts UX Restoration + KPI Navigation Hardening */}
          <div className="border-l-4 border-cyan-500 pl-4 mb-8 bg-cyan-50/20 py-4 rounded-r-xl">
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-semibold text-gray-900">
                Session: March 28, 2026 - BHPH Accounts UX Restoration + KPI Navigation Hardening
              </h4>
              <span className="text-xs text-cyan-700 font-medium bg-cyan-100 px-2 py-0.5 rounded-full">
                Completed
              </span>
            </div>
            <div className="space-y-3 text-sm text-gray-700">
              <div>
                <span className="font-medium text-blue-700">Initial Issue:</span>
                <p className="mt-1">
                  User could not manage BHPH accounts from the UI. The accounts management surface
                  had regressed and only the dashboard was accessible.
                </p>
              </div>
              <div>
                <span className="font-medium text-green-700">Key Fixes Applied:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li>
                    Restored full BHPH accounts management page at{' '}
                    <code>src/frontend/src/pages/bhph/BHPHAccountsPage.tsx</code>.
                  </li>
                  <li>
                    Added <code>listBHPHAccounts()</code> service helper in{' '}
                    <code>src/frontend/src/services/bhphApi.ts</code> for canonical{' '}
                    <code>/bhph-accounts/</code> listing.
                  </li>
                  <li>
                    Registered <code>/bhph/accounts</code> route and updated legacy{' '}
                    <code>/bhph-accounts</code> redirect in <code>src/frontend/src/App.tsx</code>.
                  </li>
                  <li>
                    Restored sidebar entry <code>BHPH Accounts</code> in{' '}
                    <code>src/frontend/src/components/layout/Sidebar.tsx</code>.
                  </li>
                  <li>
                    Wired dashboard KPI cards so <code>👥 Active Accounts</code> routes to{' '}
                    <code>/bhph/accounts</code> and <code>💰 Late Fees</code> routes to{' '}
                    <code>/bhph/late-fees</code>; added quick action <code>📂 Manage Accounts</code>
                    .
                  </li>
                </ul>
              </div>
              <div>
                <span className="font-medium text-purple-700">Validation:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li>No editor/type diagnostics in touched BHPH files.</li>
                  <li>
                    BHPH account operations are now accessible from both sidebar and dashboard KPI
                    pathways.
                  </li>
                </ul>
              </div>
              <div className="pt-2 border-t border-gray-200">
                <span className="font-medium text-indigo-700">Key Deliverables:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li>
                    <code>src/frontend/src/pages/bhph/BHPHAccountsPage.tsx</code> - restored
                    management UX for account browse/search/filter/actions
                  </li>
                  <li>
                    <code>src/frontend/src/pages/bhph/BHPHDashboardPage.tsx</code> - KPI and
                    quick-action navigation hardening
                  </li>
                  <li>
                    <code>src/frontend/src/components/layout/Sidebar.tsx</code> - BHPH Accounts
                    sidebar link restored
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Session: March 28, 2026 - Frontend Collection Route Hardening + Repo Drift Isolation */}
          <div className="border-l-4 border-emerald-500 pl-4 mb-8 bg-emerald-50/20 py-4 rounded-r-xl">
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-semibold text-gray-900">
                Session: March 28, 2026 - Frontend Collection Route Hardening + Repo Drift Isolation
              </h4>
              <span className="text-xs text-emerald-700 font-medium bg-emerald-100 px-2 py-0.5 rounded-full">
                Completed
              </span>
            </div>
            <div className="space-y-3 text-sm text-gray-700">
              <div>
                <span className="font-medium text-blue-700">Initial Issue:</span>
                <p className="mt-1">
                  User reported the recurring Deal Calculator bug: customer and vehicle search
                  requests were hitting `404` because the frontend was calling non-canonical
                  collection URLs without trailing slashes.
                </p>
              </div>
              <div>
                <span className="font-medium text-green-700">Key Fixes Applied:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li>
                    Isolated unrelated repo drift into stash{' '}
                    <code>stash@{0}: isolate-unrelated-20260328-000447</code> and restored only the
                    active Phase 2 files.
                  </li>
                  <li>
                    Created shared canonical collection helpers in{' '}
                    <code>src/frontend/src/services/collectionEndpoints.ts</code>.
                  </li>
                  <li>
                    Refactored Deal Calculator search to use shared builders and preserved
                    regression coverage.
                  </li>
                  <li>
                    Patched remaining live vehicle collection callers in Interested Vehicles modal
                    and Mobile VIN Scanner to use canonical `/vehicles/` routes.
                  </li>
                  <li>
                    Fixed the local startup mismatch by auto-starting Redis in `start-backend.sh`
                    and requiring a successful `/health` response in `start-dev.sh`.
                  </li>
                </ul>
              </div>
              <div>
                <span className="font-medium text-purple-700">Validation:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li>
                    Backend route probe confirmed no-slash collection routes returned{' '}
                    <code>404</code> while canonical slash routes resolved.
                  </li>
                  <li>
                    Focused frontend regression tests passed: <code>2</code> suites, <code>7</code>{' '}
                    tests.
                  </li>
                  <li>
                    Local backend health returned <code>200</code> after restart, with both database
                    and cache connected.
                  </li>
                  <li>
                    Frontend sweep found no remaining active <code>apiService</code> vehicle
                    collection calls using raw <code>'/vehicles'</code> or{' '}
                    <code>'/vehicles?...'</code> forms.
                  </li>
                </ul>
              </div>
              <div>
                <span className="font-medium text-red-700">Discovered Issues:</span>
                <p className="mt-1">
                  Backend `/health` still returned `503` during local startup verification even
                  though the backend root and docs were reachable. This was not part of the route
                  fix and remains separate from the frontend regression.
                </p>
              </div>
              <div className="pt-2 border-t border-gray-200">
                <span className="font-medium text-indigo-700">Key Deliverables:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li>
                    <code>src/frontend/src/services/collectionEndpoints.ts</code> - canonical
                    customer and vehicle collection endpoints
                  </li>
                  <li>
                    <code>src/frontend/src/services/__tests__/collectionEndpoints.test.ts</code> -
                    regression coverage for shared collection helpers
                  </li>
                  <li>
                    <code>src/frontend/src/pages/deals/dealCalculatorSearch.ts</code> - Deal
                    Calculator now reuses shared builders
                  </li>
                  <li>
                    <code>src/frontend/src/components/customers/InterestedVehiclesModal.tsx</code> -
                    vehicle list fetch now uses canonical `/vehicles/` query path
                  </li>
                  <li>
                    <code>src/frontend/src/pages/mobile/MobileVINScanner.tsx</code> - vehicle
                    creation now posts to canonical `/vehicles/` path
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Session: March 4, 2026 - Enum Fix + Staging Production Build */}
          <div className="border-l-4 border-blue-500 pl-4 mb-8 bg-blue-50/20 py-4 rounded-r-xl">
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-semibold text-gray-900">
                Session: March 4, 2026 — Enum Fix + Staging Production Build
              </h4>
              <span className="text-xs text-gray-500">Overnight sprint</span>
            </div>
            <div className="space-y-3 text-sm text-gray-700">
              <div>
                <span className="font-medium text-blue-700">Initial Issue:</span>
                <p className="mt-1">
                  422 Unprocessable Content on vehicle PUT — frontend sends lowercase{' '}
                  <code>status: "available"</code> but Pydantic v2 rejects it (ignores Python's{' '}
                  <code>_missing_()</code> entirely).
                </p>
              </div>
              <div>
                <span className="font-medium text-green-700">Architectural Fix Applied:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li>
                    Created <code>src/backend/core/enums.py</code> —{' '}
                    <code>CaseInsensitiveEnum</code> base class with{' '}
                    <code>__get_pydantic_core_schema__</code> (Pydantic v2 hook) +{' '}
                    <code>_missing_()</code> (SQLAlchemy/Python)
                  </li>
                  <li>
                    Updated all 25 backend enums across 9 model files to inherit{' '}
                    <code>CaseInsensitiveEnum</code>
                  </li>
                  <li>
                    Frontend status comparisons normalized to lowercase sitewide (Dashboard, Mobile,
                    CRM)
                  </li>
                  <li>
                    Customer status type aligned: removed deprecated <code>lead/prospect/sold</code>
                    , now <code>active/inactive/do_not_call</code>
                  </li>
                </ul>
              </div>
              <div>
                <span className="font-medium text-purple-700">Staging Infrastructure Fixed:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li>
                    Resolved system Redis conflict (port 6379) — removed Docker Redis port binding
                  </li>
                  <li>
                    Fixed DATABASE_URL: <code>simpledealer_dev</code> →{' '}
                    <code>simpledealer_prod</code>
                  </li>
                  <li>Fixed nginx port mismatch: backend 18000, frontend 18080</li>
                  <li>Resolved "Invalid Host header" from CRA dev server</li>
                  <li>
                    Converted frontend from CRA dev server to production build (multi-stage
                    Dockerfile + <code>serve -s build</code>)
                  </li>
                </ul>
              </div>
              <div>
                <span className="font-medium text-orange-700">
                  Browser Cache Issue — Solved Permanently:
                </span>
                <p className="mt-1">
                  CRA dev server uses single <code>bundle.js</code> filename — browsers cache it
                  indefinitely. Production build generates content-hashed files (
                  <code>main.4b9e4ce6.js</code>) that auto-bust cache on every deploy.
                </p>
              </div>
              <div className="pt-2 border-t border-gray-200">
                <span className="font-medium text-indigo-700">Key Deliverables:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li>
                    <code>src/backend/core/enums.py</code> — permanent enum fix
                    (CaseInsensitiveEnum)
                  </li>
                  <li>
                    <code>src/frontend/Dockerfile</code> (staging) — multi-stage production build
                  </li>
                  <li>
                    <code>docker-compose.yml</code> (staging) — ports, Redis, DB URL, prod frontend
                    config
                  </li>
                  <li>All 9 backend model files — enum inheritance updated</li>
                  <li>staging.carlotpilot.com — live and verified ✅</li>
                </ul>
              </div>
              <div>
                <span className="font-medium text-gray-600">Note:</span>
                <p className="mt-1">
                  GitHub push blocked — PAT lacks <code>workflow</code> scope (deploy.yml in commit
                  history). Staging synced via rsync. To fix: update PAT in GitHub Settings →
                  Developer settings → Personal access tokens → add <code>workflow</code> scope.
                </p>
              </div>
            </div>
          </div>

          {/* Session: March 2, 2026 - Deep Audit + Sprint Execution */}
          <div className="border-l-4 border-violet-500 pl-4 mb-8 bg-violet-50/20 py-4 rounded-r-xl">
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <SparklesIcon className="w-4 h-4 text-violet-700" />
                Session: March 2, 2026 - Deep Audit + Sprint Execution
              </h4>
              <span className="text-xs text-violet-700 font-medium bg-violet-100 px-2 py-0.5 rounded-full">
                Completed
              </span>
            </div>

            <div className="space-y-3 text-sm text-gray-700">
              <div>
                <span className="font-medium text-blue-700">Initial Request:</span>
                <p className="mt-1">
                  Deep workspace audit to assess OAuth readiness + plan and execute next sprint
                  autonomously.
                </p>
              </div>

              <div>
                <span className="font-medium text-green-700">OAuth Audit Finding:</span>
                <p className="mt-1">
                  System is NOT social-login OAuth ready. Facebook OAuth exists only for marketplace
                  (inventory posting to FB Business Pages). JWT email/password auth is complete.
                  Recommendation: keep email/password for now, defer Google OAuth to Q2. No{' '}
                  <code>authlib</code> or <code>python-social-auth</code> installed.
                </p>
              </div>

              <div>
                <span className="font-medium text-purple-700">jCodeMunch MCP Installed:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li>
                    Installed in isolated venv at <code>~/.jcodemunch-venv/</code> to avoid
                    starlette conflict with backend
                  </li>
                  <li>
                    Configured via <code>.mcp.json</code> (project scope) and{' '}
                    <code>~/.claude.json</code> (user scope)
                  </li>
                  <li>
                    ANTHROPIC_API_KEY and GITHUB_TOKEN exported to <code>~/.bashrc</code>
                  </li>
                </ul>
              </div>

              <div>
                <span className="font-medium text-orange-700">Sprint Executed (Phases 1–4):</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li>
                    Phase 1 ✅ — 109 files committed: public website, bilingual UX, document forms,
                    settings expansion
                  </li>
                  <li>
                    Phase 2 ⚠️ — Production deployment BLOCKED (no SSH key access); user must deploy
                    manually per runbook
                  </li>
                  <li>
                    Phase 3 ✅ — Deal → BHPH pipeline:{' '}
                    <code>POST /deals/{'{id}'}/close-to-bhph</code> endpoint + frontend button
                  </li>
                  <li>
                    Phase 4 ✅ — Document email: <code>POST /documents/generate-and-email</code> +
                    DocumentCenterPage email enabled
                  </li>
                </ul>
              </div>

              <div>
                <span className="font-medium text-red-700">
                  Production Deployment (Needs User Action):
                </span>
                <p className="mt-1">
                  SSH to 23.227.173.230 and 172.93.50.239, run docker-compose.prod.yml, alembic
                  upgrade head. See OPENCLAW_RUNBOOK for full steps.
                </p>
              </div>

              <div className="pt-2 border-t border-gray-200">
                <span className="font-medium text-indigo-700">Key Deliverables:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li>
                    <code>deals.py</code> — POST /deals/{'{id}'}/close-to-bhph (wires
                    AccountLifecycleService)
                  </li>
                  <li>
                    <code>documents.py</code> — POST /documents/generate-and-email (in-memory PDF +
                    SMTP send)
                  </li>
                  <li>
                    <code>DealDetailPage.tsx</code> — Close Deal button for BHPH deals
                  </li>
                  <li>
                    <code>DocumentCenterPage.tsx</code> — Email option enabled (showEmailOption:
                    true)
                  </li>
                  <li>
                    <code>tasks/SPRINT_AUDIT_2026_03_02.md</code> — Full audit report (prod
                    readiness: 72/100)
                  </li>
                  <li>
                    <code>.mcp.json</code> + <code>~/.claude.json</code> — jCodeMunch MCP configured
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Session: February 26, 2026 - BHPH E2E Stabilization Pass */}
          <div className="border-l-4 border-lime-500 pl-4 mb-8 bg-lime-50/20 py-4 rounded-r-xl">
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <SparklesIcon className="w-4 h-4 text-lime-700" />
                Session: February 26, 2026 - BHPH E2E Stabilization Pass
              </h4>
              <span className="text-xs text-lime-700 font-medium bg-lime-100 px-2 py-0.5 rounded-full">
                Completed
              </span>
            </div>

            <div className="space-y-3 text-sm text-gray-700">
              <div>
                <span className="font-medium text-blue-700">Initial Issue:</span>
                <p className="mt-1">
                  BHPH E2E failures were concentrated in stale suite assumptions (old routes, old
                  selectors, and non-existent dashboard tab/modal flows), blocking meaningful
                  regression validation.
                </p>
              </div>

              <div>
                <span className="font-medium text-green-700">Key Fixes Applied:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc text-xs">
                  <li>
                    Aligned workflows suite to current local auth/base URL and current BHPH route
                    behavior.
                  </li>
                  <li>
                    Replaced brittle selectors with deterministic role/text patterns and added
                    empty-state-safe account-detail handling.
                  </li>
                  <li>
                    Rewrote dashboard suite to validate current BHPH dashboard UX and quick-action
                    navigation instead of legacy tab/modal behavior.
                  </li>
                </ul>
              </div>

              <div className="pt-2 border-t border-gray-100">
                <span className="font-medium text-indigo-700">Key Deliverables:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc text-xs">
                  <li>
                    <code>src/frontend/tests/bhph-workflows.spec.ts</code> - stabilized and passing
                    on Chromium.
                  </li>
                  <li>
                    <code>src/frontend/tests/bhph-dashboard.spec.ts</code> - modernized for current
                    dashboard implementation.
                  </li>
                  <li>
                    <code>docs/SESSION_STORYBOOK.md</code> - session continuity updated with pass
                    evidence.
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Session: February 26, 2026 - Website Builder E2E Selector Stabilization */}
          <div className="border-l-4 border-emerald-500 pl-4 mb-8 bg-emerald-50/20 py-4 rounded-r-xl">
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <SparklesIcon className="w-4 h-4 text-emerald-700" />
                Session: February 26, 2026 - Website Builder E2E Selector Stabilization
              </h4>
              <span className="text-xs text-emerald-700 font-medium bg-emerald-100 px-2 py-0.5 rounded-full">
                Completed
              </span>
            </div>

            <div className="space-y-3 text-sm text-gray-700">
              <div>
                <span className="font-medium text-blue-700">Initial Issue:</span>
                <p className="mt-1">
                  `website-builder.spec.ts` was failing due strict selector ambiguity (`Settings`)
                  and non-functional label-based field selection (`Headline`) after
                  navigation/reload.
                </p>
              </div>

              <div>
                <span className="font-medium text-green-700">Key Fixes Applied:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc text-xs">
                  <li>
                    Updated settings navigation assertion to use heading role and scoped nav
                    buttons.
                  </li>
                  <li>
                    Replaced `getByLabel('Headline')` with deterministic selector
                    `label:has-text("Headline") + input[type="text"]`.
                  </li>
                  <li>Validated the patched spec in Chromium as a focused regression check.</li>
                </ul>
              </div>

              <div className="pt-2 border-t border-gray-100">
                <span className="font-medium text-indigo-700">Key Deliverables:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc text-xs">
                  <li>
                    <code>src/frontend/tests/website-builder.spec.ts</code> - deterministic
                    selectors for website builder navigation and headline field edit/verify flow.
                  </li>
                  <li>
                    <code>docs/SESSION_STORYBOOK.md</code> - session log entry with validation
                    result.
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Session: February 25, 2026 - SaaS Template Alignment for UnitedAutoBrokers */}
          <div className="border-l-4 border-indigo-500 pl-4 mb-8 bg-indigo-50/20 py-4 rounded-r-xl">
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <SparklesIcon className="w-4 h-4 text-indigo-700" />
                Session: February 25, 2026 - SaaS Template Alignment for UnitedAutoBrokers
              </h4>
              <span className="text-xs text-indigo-700 font-medium bg-indigo-100 px-2 py-0.5 rounded-full">
                Completed
              </span>
            </div>

            <div className="space-y-3 text-sm text-gray-700">
              <div>
                <span className="font-medium text-blue-700">Initial Issue:</span>
                <p className="mt-1">
                  Ensure the new unitedautobrokers public webapp is managed as a SaaS website
                  template from `Settings &gt; Dealership &gt; Website`, consistent with the
                  platform model.
                </p>
              </div>

              <div>
                <span className="font-medium text-green-700">Key Fixes Applied:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc text-xs">
                  <li>
                    Updated public rendering to respect `settings.templateId` as the source of
                    truth.
                  </li>
                  <li>Added new template option `saas_bilingual` in backend template catalog.</li>
                  <li>Added matching fallback template option in Settings UI local defaults.</li>
                  <li>
                    Kept existing templates intact while enabling a template-driven switch to the
                    new bilingual webapp.
                  </li>
                </ul>
              </div>

              <div className="pt-2 border-t border-gray-100">
                <span className="font-medium text-indigo-700">Key Deliverables:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc text-xs">
                  <li>
                    <code>src/frontend/src/pages/PublicWebsitePage.tsx</code> - template-based
                    public rendering path.
                  </li>
                  <li>
                    <code>src/backend/api/v1/endpoints/website_builder.py</code> - added
                    `saas_bilingual` template catalog entry.
                  </li>
                  <li>
                    <code>src/frontend/src/pages/settings/SettingsPage.tsx</code> - template
                    fallback list includes `saas_bilingual`.
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Session: February 25, 2026 - Public Lead Endpoint Hardening */}
          <div className="border-l-4 border-teal-500 pl-4 mb-8 bg-teal-50/20 py-4 rounded-r-xl">
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <SparklesIcon className="w-4 h-4 text-teal-700" />
                Session: February 25, 2026 - Public Lead Endpoint Hardening
              </h4>
              <span className="text-xs text-teal-700 font-medium bg-teal-100 px-2 py-0.5 rounded-full">
                Completed
              </span>
            </div>

            <div className="space-y-3 text-sm text-gray-700">
              <div>
                <span className="font-medium text-blue-700">Initial Issue:</span>
                <p className="mt-1">
                  Protect the new public lead endpoint from bot spam and accidental duplicate
                  submissions before production traffic ramps up.
                </p>
              </div>

              <div>
                <span className="font-medium text-green-700">Key Fixes Applied:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc text-xs">
                  <li>Added API rate limiting for public lead capture requests.</li>
                  <li>Added honeypot + form timing guards for bot resistance.</li>
                  <li>Added validation hardening for name/phone/email/message input.</li>
                  <li>Added duplicate suppression window for repeated submissions.</li>
                </ul>
              </div>

              <div className="pt-2 border-t border-gray-100">
                <span className="font-medium text-indigo-700">Key Deliverables:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc text-xs">
                  <li>
                    <code>src/backend/api/v1/endpoints/website_builder.py</code> - rate-limited
                    anti-spam lead endpoint.
                  </li>
                  <li>
                    <code>src/frontend/src/pages/public/ContactPage.tsx</code> - anti-spam metadata
                    on form submit.
                  </li>
                  <li>
                    <code>src/frontend/src/pages/public/FinancingPage.tsx</code> - anti-spam
                    metadata on credit request submit.
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Session: February 25, 2026 - Public Website Phase 3 (Live Lead Wiring + Mobile/Media UX) */}
          <div className="border-l-4 border-cyan-500 pl-4 mb-8 bg-cyan-50/20 py-4 rounded-r-xl">
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <SparklesIcon className="w-4 h-4 text-cyan-700" />
                Session: February 25, 2026 - Public Website Phase 3 (Live Lead Wiring + Mobile/Media
                UX)
              </h4>
              <span className="text-xs text-cyan-700 font-medium bg-cyan-100 px-2 py-0.5 rounded-full">
                Completed
              </span>
            </div>

            <div className="space-y-3 text-sm text-gray-700">
              <div>
                <span className="font-medium text-blue-700">Initial Issue:</span>
                <p className="mt-1">
                  Complete production-ready public UX by wiring real lead capture, improving mobile
                  conversion paths, and upgrading vehicle media presentation.
                </p>
              </div>

              <div>
                <span className="font-medium text-green-700">Key Fixes Applied:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc text-xs">
                  <li>
                    Added a new backend public lead endpoint at{' '}
                    <code>/api/v1/website/leads/public</code>.
                  </li>
                  <li>
                    Connected Contact + Financing forms to submit live leads with intent and
                    optional vehicle context.
                  </li>
                  <li>Added sticky mobile CTA bar for Call + Apply on public pages.</li>
                  <li>
                    Enhanced vehicle detail with multi-image gallery and selectable thumbnails.
                  </li>
                </ul>
              </div>

              <div className="pt-2 border-t border-gray-100">
                <span className="font-medium text-indigo-700">Key Deliverables:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc text-xs">
                  <li>
                    <code>src/backend/api/v1/endpoints/website_builder.py</code> - public lead
                    capture endpoint.
                  </li>
                  <li>
                    <code>src/frontend/src/pages/public/ContactPage.tsx</code> - live lead
                    submission.
                  </li>
                  <li>
                    <code>src/frontend/src/pages/public/FinancingPage.tsx</code> - credit request
                    lead submission.
                  </li>
                  <li>
                    <code>src/frontend/src/pages/public/VehicleDetailPage.tsx</code> - multi-image
                    vehicle gallery.
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Session: February 25, 2026 - Public Website Phase 2 (Bilingual MVP) */}
          <div className="border-l-4 border-blue-500 pl-4 mb-8 bg-blue-50/20 py-4 rounded-r-xl">
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <SparklesIcon className="w-4 h-4 text-blue-700" />
                Session: February 25, 2026 - Public Website Phase 2 (Bilingual MVP)
              </h4>
              <span className="text-xs text-blue-700 font-medium bg-blue-100 px-2 py-0.5 rounded-full">
                Completed
              </span>
            </div>

            <div className="space-y-3 text-sm text-gray-700">
              <div>
                <span className="font-medium text-blue-700">Initial Issue:</span>
                <p className="mt-1">
                  Build a modern bilingual (English/Spanish) public website aligned with
                  SimpleDealer, without cloning the legacy CFS website.
                </p>
              </div>

              <div>
                <span className="font-medium text-green-700">Key Fixes Applied:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc text-xs">
                  <li>
                    Implemented public pages for Home, Inventory, Vehicle Detail, Financing, and
                    Contact.
                  </li>
                  <li>
                    Added EN/ES language toggle with centralized dictionary-based text rendering.
                  </li>
                  <li>
                    Added inventory search + filtering (make, body style, max price) and detail
                    routing via <code>/inventory/:vehicleId</code>.
                  </li>
                  <li>
                    Refactored public UI into reusable components (header, language switcher,
                    filters, cards).
                  </li>
                </ul>
              </div>

              <div className="pt-2 border-t border-gray-100">
                <span className="font-medium text-indigo-700">Key Deliverables:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc text-xs">
                  <li>
                    <code>src/frontend/src/pages/public/PublicSiteShell.tsx</code> - routed
                    bilingual public shell.
                  </li>
                  <li>
                    <code>src/frontend/src/pages/public/copy.ts</code> - EN/ES public copy
                    dictionary.
                  </li>
                  <li>
                    <code>src/frontend/src/pages/public/InventoryPage.tsx</code> - filterable
                    inventory experience.
                  </li>
                  <li>
                    <code>src/frontend/src/pages/public/VehicleDetailPage.tsx</code> - public
                    vehicle detail with CTAs.
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Session: February 25, 2026 - Sprint Session Kickoff */}
          <div className="border-l-4 border-violet-500 pl-4 mb-8 bg-violet-50/20 py-4 rounded-r-xl">
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <SparklesIcon className="w-4 h-4 text-violet-700" />
                Session: February 25, 2026 - Sprint Session Kickoff
              </h4>
              <span className="text-xs text-violet-700 font-medium bg-violet-100 px-2 py-0.5 rounded-full">
                In Progress
              </span>
            </div>

            <div className="space-y-3 text-sm text-gray-700">
              <div>
                <span className="font-medium text-blue-700">Initial Issue:</span>
                <p className="mt-1">
                  Start a new sprint session with clean continuity and strict alignment to the
                  deployment sprint source of truth.
                </p>
              </div>

              <div>
                <span className="font-medium text-green-700">Key Fixes Applied:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc text-xs">
                  <li>
                    Re-validated active sprint scope against{' '}
                    <code>.claude/tasks/NEXT_SPRINT.md</code> and confirmed <code>TODO.md</code>{' '}
                    alignment.
                  </li>
                  <li>
                    Synced kickoff logs across continuity files so this session can proceed with
                    traceable deployment checkpoints.
                  </li>
                </ul>
              </div>

              <div className="pt-2 border-t border-gray-100">
                <span className="font-medium text-indigo-700">Key Deliverables:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc text-xs">
                  <li>
                    <code>docs/SESSION_STORYBOOK.md</code> - Feb 25 kickoff entry.
                  </li>
                  <li>
                    <code>.claude/session_context.md</code> - refreshed session focus and
                    priorities.
                  </li>
                  <li>
                    <code>src/frontend/src/pages/tracker/SuperTrackerPage.tsx</code> - kickoff card
                    + latest sync update.
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Session: February 24, 2026 - UnitedAutoBrokers Web App Runbook */}
          <div className="border-l-4 border-sky-500 pl-4 mb-8 bg-sky-50/20 py-4 rounded-r-xl">
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <BoltIcon className="w-4 h-4 text-sky-700" />
                Session: February 24, 2026 - UnitedAutoBrokers Web App Runbook
              </h4>
              <span className="text-xs text-sky-700 font-medium bg-sky-100 px-2 py-0.5 rounded-full">
                Completed
              </span>
            </div>

            <div className="space-y-3 text-sm text-gray-700">
              <div>
                <span className="font-medium text-blue-700">Initial Issue:</span>
                <p className="mt-1">
                  Needed a clean Ubuntu 24.04 setup to host a new dynamic{' '}
                  <code>unitedautobrokers.online</code> + <code>www</code> web app, while keeping
                  CarsForSale FTP delivery on <code>ftp.staging.unitedautobrokers.online</code>.
                </p>
              </div>

              <div>
                <span className="font-medium text-green-700">Key Fixes Applied:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc text-xs">
                  <li>
                    Produced a copy/paste OpenClaw runbook for Cloudflare DNS/SSL, UFW ports,
                    Docker, Caddy, and optional vsftpd passive ports.
                  </li>
                  <li>
                    Documented the recommended pattern for inventory fetching: proxy{' '}
                    <code>/api/*</code> from the website origin to the SimpleDealer SaaS (e.g.{' '}
                    <code>carlotpilot.com</code>) and forward <code>X-Forwarded-Host</code> so the
                    SaaS can resolve tenants by domain.
                  </li>
                </ul>
              </div>

              <div className="pt-2 border-t border-gray-100">
                <span className="font-medium text-indigo-700">Key Deliverables:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc text-xs">
                  <li>
                    <code>docs/OPENCLAW_RUNBOOK_UNITEDAUTOBROKERS_UBUNTU24.md</code> - end-to-end
                    server setup + deploy guidance
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Session: February 23, 2026 - Staging Deployment Mismatch Fix */}
          <div className="border-l-4 border-emerald-500 pl-4 mb-8 bg-emerald-50/20 py-4 rounded-r-xl">
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <ServerIcon className="w-4 h-4 text-emerald-700" />
                Session: February 23, 2026 - Staging Deployment Mismatch Fix
              </h4>
              <span className="text-xs text-emerald-700 font-medium bg-emerald-100 px-2 py-0.5 rounded-full">
                Completed
              </span>
            </div>

            <div className="space-y-3 text-sm text-gray-700">
              <div>
                <span className="font-medium text-blue-700">Initial Issue:</span>
                <p className="mt-1">
                  Staging checks returned <code>502 Bad Gateway</code> on{' '}
                  <code>staging.carlotpilot.com/health</code>,<code>526</code> on{' '}
                  <code>dev.carlotpilot.com</code>, and TLS SNI mismatch on{' '}
                  <code>staging.carcopilot.com</code>. Deployment tooling also defaulted to the
                  wrong server/compose stack.
                </p>
              </div>

              <div>
                <span className="font-medium text-green-700">Key Fixes Applied:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc text-xs">
                  <li>
                    Updated <code>deploy-staging.sh</code> to support{' '}
                    <code>DEPLOY_STACK=prod|virtualmin</code> and select the correct compose file.
                  </li>
                  <li>
                    Removed hard-coded health-check URLs; script now prints curl checks based on{' '}
                    <code>STAGING_DOMAIN</code>.
                  </li>
                  <li>
                    Documented the 502/526/SNI troubleshooting workflow in{' '}
                    <code>docs/TROUBLESHOOTING.md</code>.
                  </li>
                </ul>
              </div>

              <div className="pt-2 border-t border-gray-100">
                <span className="font-medium text-indigo-700">Key Deliverables:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc text-xs">
                  <li>
                    <code>deploy-staging.sh</code> - Safer, stack-aware deployment.
                  </li>
                  <li>
                    <code>docs/TROUBLESHOOTING.md</code> - 502/526/SNI guidance.
                  </li>
                  <li>
                    <code>docs/SESSION_STORYBOOK.md</code> - Session log entry.
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Session: February 24, 2026 - Staging Backend Startup Crash (ENUM Race) */}
          <div className="border-l-4 border-amber-500 pl-4 mb-8 bg-amber-50/20 py-4 rounded-r-xl">
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <ServerIcon className="w-4 h-4 text-amber-700" />
                Session: February 24, 2026 - Staging Backend Startup Crash (ENUM Race)
              </h4>
              <span className="text-xs text-amber-700 font-medium bg-amber-100 px-2 py-0.5 rounded-full">
                Completed
              </span>
            </div>

            <div className="space-y-3 text-sm text-gray-700">
              <div>
                <span className="font-medium text-blue-700">Initial Issue:</span>
                <p className="mt-1">
                  After importing/restoring the staging PostgreSQL DB, the backend began exiting on
                  startup and <code>/health</code> returned an empty reply. Logs showed duplicate
                  ENUM creation attempts (e.g. <code>CREATE TYPE customerstatus AS ENUM ...</code>).
                </p>
              </div>

              <div>
                <span className="font-medium text-green-700">Key Fixes Applied:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc text-xs">
                  <li>
                    Added a PostgreSQL advisory lock around startup{' '}
                    <code>SQLModel.metadata.create_all()</code> so multiple Uvicorn workers can’t
                    race on ENUM creation.
                  </li>
                  <li>
                    Documented symptom + remediation steps in <code>docs/TROUBLESHOOTING.md</code>.
                  </li>
                </ul>
              </div>

              <div className="pt-2 border-t border-gray-100">
                <span className="font-medium text-indigo-700">Key Deliverables:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc text-xs">
                  <li>
                    <code>src/backend/core/database.py</code> - advisory-locked startup schema init.
                  </li>
                  <li>
                    <code>docs/TROUBLESHOOTING.md</code> - duplicate ENUM crash guidance.
                  </li>
                  <li>
                    <code>docs/SESSION_STORYBOOK.md</code> - session log entry.
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Session: April 6-7, 2026 - UAB Blog + AI Chatbot Launch & CI/CD Repair */}
          <div className="border-l-4 border-green-500 pl-4 mb-8 bg-green-50/20 py-4 rounded-r-xl">
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <SparklesIcon className="w-4 h-4 text-green-600" />
                Session: April 6–7, 2026 — UAB Blog + AI Chatbot Launch & CI/CD Repair
              </h4>
              <span className="text-xs text-gray-500">~8h session</span>
            </div>
            <div className="space-y-3 text-sm text-gray-700">
              <div>
                <span className="font-medium text-blue-700">Initial Issue:</span>
                <p className="mt-1">
                  UAB site showed 502 Bad Gateway after a deployment. 91 commits had never been
                  pushed to GitHub. Pushed the feature/uab-blog-chatbot branch and discovered a
                  cascade of CI/CD failures.
                </p>
              </div>
              <div>
                <span className="font-medium text-green-700">What Was Shipped:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li>
                    6 bilingual blog posts (EN + ES): buying checklist, maintenance, BHPH,
                    first-time buyers, red flags, credit building
                  </li>
                  <li>32 AI-generated hero + section images (webp, gpt-image-1)</li>
                  <li>
                    AI chatbot bubble ("Ask Us") — floating → fullscreen, SSE streaming,
                    configurable per-tenant from CRM Settings
                  </li>
                  <li>Blog nav link in public header, responsive vehicle carousel on homepage</li>
                  <li>GHCR_TOKEN configured on both servers for automated image pulls</li>
                </ul>
              </div>
              <div>
                <span className="font-medium text-red-700">CI/CD Fixes (7 issues):</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li>Git remote pointed to wrong repo (unitedautobrokers.git)</li>
                  <li>
                    Docker image name uppercase — github.repository has capital S in SimpleDealer
                  </li>
                  <li>Untracked BlogPage.tsx and publicSitePaths.ts not in CI build</li>
                  <li>TS2322: CustomerLoginPage given invalid language prop</li>
                  <li>ESLint no-constant-condition on SSE while(true) loop</li>
                  <li>CI test used full SHA tag that doesn't exist (only :main is tagged)</li>
                  <li>Frontend CI test ran `node --version` inside nginx container (no node)</li>
                </ul>
              </div>
              <div>
                <span className="font-medium text-orange-700">Critical Lessons:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li>
                    Local fixes that are never committed will never deploy — always diff before
                    assuming a fix is live
                  </li>
                  <li>
                    `docker restart` reuses old image — must stop/rm/recreate to pick up new image
                  </li>
                  <li>
                    frontend_override volume can overwrite new builds — clear it before redeployment
                  </li>
                  <li>Port mapping: Dockerfile.prod nginx listens on 80, not 3000 (dev nginx)</li>
                </ul>
              </div>
              <div className="pt-2 border-t border-gray-200">
                <span className="font-medium text-indigo-700">Key Deliverables:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li>
                    <code>src/frontend/src/pages/public/blogData.ts</code> — 6 full bilingual posts
                  </li>
                  <li>
                    <code>src/frontend/src/components/public/ChatBubble.tsx</code> +{' '}
                    <code>PublicChatWidget.tsx</code>
                  </li>
                  <li>
                    <code>src/backend/api/v1/endpoints/public.py</code> — SSE chat endpoint
                  </li>
                  <li>
                    <code>.github/workflows/docker-build.yml</code> — 5 CI fixes
                  </li>
                  <li>
                    <code>src/frontend/src/pages/PublicWebsitePage.tsx</code> — vehicles/public
                    endpoint fix
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Session: February 27, 2026 - Bill of Sale Editable Workflow + Header Sync */}
          <div className="border-l-4 border-indigo-500 pl-4 mb-8 bg-indigo-50/20 py-4 rounded-r-xl">
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <SparklesIcon className="w-4 h-4 text-indigo-600" />
                Session: February 27, 2026 - Bill of Sale Editable Workflow + Header Sync
              </h4>
              <span className="text-xs text-gray-500">Current Session</span>
            </div>

            <div className="space-y-3 text-sm text-gray-700">
              <div>
                <span className="font-medium text-blue-700">Initial Issue:</span>
                <p className="mt-1">
                  Bill of Sale needed to mirror backup CSV fields, allow full edits before output,
                  and include dealer header information pulled from Dealership General settings.
                </p>
              </div>

              <div>
                <span className="font-medium text-green-700">Key Fixes Applied:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc text-xs">
                  <li>
                    Expanded Bill of Sale required fields to align with the CSV-backed structure
                    used by backup exports.
                  </li>
                  <li>
                    Updated document endpoints to inject dealer name/address/phone from persisted
                    settings into preview and generation flows.
                  </li>
                  <li>
                    Implemented multi-action document modal flow for PDF download, print, and email
                    using edited form data.
                  </li>
                  <li>
                    Reworked Dealership General settings UI to load/save dealer phone and physical
                    address fields.
                  </li>
                </ul>
              </div>

              <div>
                <span className="font-medium text-purple-700">Bill of Sale Workflow:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc text-xs">
                  <li>
                    Document Center now opens editable Bill of Sale form with preview-prefilled
                    values.
                  </li>
                  <li>All fields remain editable before output action is selected.</li>
                  <li>
                    Email flow generates and saves the document, then sends to requested recipient.
                  </li>
                </ul>
              </div>

              <div className="pt-2 border-t border-gray-100">
                <span className="font-medium text-indigo-700">Key Deliverables:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc text-xs">
                  <li>
                    <code>src/backend/services/document_templates.py</code> - Bill of Sale
                    CSV-aligned field/template update.
                  </li>
                  <li>
                    <code>src/backend/api/v1/endpoints/documents.py</code> - dealer header injection
                    for preview/generate paths.
                  </li>
                  <li>
                    <code>src/backend/api/v1/endpoints/settings.py</code> - partial dealership
                    settings upsert handling.
                  </li>
                  <li>
                    <code>src/frontend/src/components/documents/DocumentFormModal.tsx</code> -
                    editable action modal (download/print/email).
                  </li>
                  <li>
                    <code>src/frontend/src/components/documents/DocumentGenerator.tsx</code> -
                    preview-prefill + action routing.
                  </li>
                  <li>
                    <code>src/frontend/src/pages/documents/DocumentCenterPage.tsx</code> -
                    modal-based Edit & Generate flow.
                  </li>
                  <li>
                    <code>src/frontend/src/pages/settings/SettingsPage.tsx</code> - General form
                    with dealer phone/address persistence.
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Session: February 26, 2026 - Defensive Helmet Context Hardening */}
          <div className="border-l-4 border-blue-400 pl-4 mb-8 bg-blue-50/20 py-4 rounded-r-xl">
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <BoltIcon className="w-4 h-4 text-blue-600" />
                Session: February 26, 2026 - Defensive Helmet Context Hardening
              </h4>
              <span className="text-xs text-blue-600 font-medium bg-blue-100 px-2 py-0.5 rounded-full">
                Completed
              </span>
            </div>

            <div className="space-y-3 text-sm text-gray-700">
              <div>
                <span className="font-medium text-blue-700">Initial Issue:</span>
                <p className="mt-1">
                  Production stack traces still showed{' '}
                  <code>Cannot read properties of undefined (reading 'add')</code> in a minified{' '}
                  <code>react-helmet-async</code> path.
                </p>
              </div>

              <div>
                <span className="font-medium text-green-700">Key Fixes Applied:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc text-xs">
                  <li>
                    Added local <code>HelmetProvider</code> around <code>WebsiteSEO</code> metadata
                    rendering for defensive context safety.
                  </li>
                  <li>
                    Kept the existing root-level <code>HelmetProvider</code> in <code>App.tsx</code>{' '}
                    unchanged.
                  </li>
                  <li>Validated no editor/type errors in updated file.</li>
                </ul>
              </div>

              <div className="pt-2 border-t border-gray-100">
                <span className="font-medium text-indigo-700">Deliverables:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc text-xs">
                  <li>
                    <code>src/frontend/src/components/website/WebsiteTemplate.tsx</code> - defensive
                    local <code>HelmetProvider</code> wrapper for SEO metadata.
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Session: February 26, 2026 - Public Placeholder 404 Cleanup */}
          <div className="border-l-4 border-blue-400 pl-4 mb-8 bg-blue-50/20 py-4 rounded-r-xl">
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <BoltIcon className="w-4 h-4 text-blue-600" />
                Session: February 26, 2026 - Public Placeholder 404 Cleanup
              </h4>
              <span className="text-xs text-blue-600 font-medium bg-blue-100 px-2 py-0.5 rounded-full">
                Completed
              </span>
            </div>

            <div className="space-y-3 text-sm text-gray-700">
              <div>
                <span className="font-medium text-blue-700">Initial Issue:</span>
                <p className="mt-1">
                  Browser console showed repeated <code>GET /placeholder-vehicle.jpg 404</code>{' '}
                  errors on public vehicle pages/templates.
                </p>
              </div>

              <div>
                <span className="font-medium text-green-700">Key Fixes Applied:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc text-xs">
                  <li>
                    Replaced all missing placeholder fallbacks with an existing static asset path:{' '}
                    <code>/images/CarLotPilot_Logo.png</code>.{' '}
                    <strong>Superseded 2026-05-26 by the N1 white-label leak sweep</strong> —
                    public-surface fallbacks now point at{' '}
                    <code>/images/vehicle_placeholder.svg</code>, a non-branded asset; the
                    CarLotPilot logo is retained for the marketing landing page only.
                  </li>
                  <li>
                    Confirmed no remaining references to <code>/placeholder-vehicle.jpg</code> in
                    frontend source.
                  </li>
                  <li>Validated changed files report no editor/type errors.</li>
                </ul>
              </div>

              <div className="pt-2 border-t border-gray-100">
                <span className="font-medium text-indigo-700">Deliverables:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc text-xs">
                  <li>
                    <code>src/frontend/src/components/website/WebsiteTemplate.tsx</code> - default
                    image fallback updated.
                  </li>
                  <li>
                    <code>src/frontend/src/components/website/public/VehicleCardPublic.tsx</code> -
                    image error fallback updated.
                  </li>
                  <li>
                    <code>
                      src/frontend/src/components/website/templates/BudgetFriendlyTemplate.tsx
                    </code>{' '}
                    - fallback src/onError updated.
                  </li>
                  <li>
                    <code>src/frontend/src/pages/public/VehicleDetailPage.tsx</code> -
                    gallery/default fallbacks updated.
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Session: February 26, 2026 - Public Runtime Crash Fix */}
          <div className="border-l-4 border-blue-400 pl-4 mb-8 bg-blue-50/20 py-4 rounded-r-xl">
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <BoltIcon className="w-4 h-4 text-blue-600" />
                Session: February 26, 2026 - Public Runtime Crash Fix
              </h4>
              <span className="text-xs text-blue-600 font-medium bg-blue-100 px-2 py-0.5 rounded-full">
                Completed
              </span>
            </div>

            <div className="space-y-3 text-sm text-gray-700">
              <div>
                <span className="font-medium text-blue-700">Initial Issue:</span>
                <p className="mt-1">
                  Production public site crashed with{' '}
                  <code>TypeError: Cannot read properties of undefined (reading 'add')</code>
                  and was caught by <code>ErrorBoundary</code>.
                </p>
              </div>

              <div>
                <span className="font-medium text-green-700">Key Fixes Applied:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc text-xs">
                  <li>
                    <strong>Root Cause Confirmation</strong>: Failure matched{' '}
                    <code>react-helmet-async</code> dispatcher path where
                    <code>helmetInstances.add(...)</code> is called without provider context.
                  </li>
                  <li>
                    <strong>Root Wrapper Fix</strong>: Wrapped the application tree in{' '}
                    <code>HelmetProvider</code> in <code>App.tsx</code>.
                  </li>
                  <li>
                    <strong>Validation</strong>: Confirmed no editor/type errors in the updated app
                    root file.
                  </li>
                </ul>
              </div>

              <div className="pt-2 border-t border-gray-100">
                <span className="font-medium text-indigo-700">Deliverables:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc text-xs">
                  <li>
                    <code>src/frontend/src/App.tsx</code> - Restored <code>HelmetProvider</code> at
                    root.
                  </li>
                  <li>
                    <code>docs/SESSION_STORYBOOK.md</code> - Added session continuity entry.
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Session: February 19, 2026 - unitedautobrokers.online Website Recovery */}
          <div className="border-l-4 border-blue-400 pl-4 mb-8 bg-blue-50/20 py-4 rounded-r-xl">
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <BoltIcon className="w-4 h-4 text-blue-600" />
                Session: February 19, 2026 - Website Recovery
              </h4>
              <span className="text-xs text-blue-600 font-medium bg-blue-100 px-2 py-0.5 rounded-full">
                Completed
              </span>
            </div>

            <div className="space-y-3 text-sm text-gray-700">
              <div>
                <span className="font-medium text-blue-700">Initial Issue:</span>
                <p className="mt-1">
                  Public dealership website <code>https://unitedautobrokers.online/</code> displayed
                  "Something went wrong" crash via the <code>ErrorBoundary</code>.
                </p>
              </div>

              <div>
                <span className="font-medium text-green-700">Key Fixes Applied:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc text-xs">
                  <li>
                    <strong>Root Wrapper Fix</strong>: Added <code>HelmetProvider</code> from
                    <code>react-helmet-async</code> to the root of <code>App.tsx</code>.
                  </li>
                  <li>
                    <strong>Tenant Verification</strong>: Confirmed correct <code>tenant_id</code>{' '}
                    configuration matches the domain in the production database.
                  </li>
                  <li>
                    <strong>Cutover Check</strong>: If the root domain still shows the crash after
                    deployment, verify Cloudflare/DNS points the root domain at this stack
                    (otherwise it may still be served by a legacy origin).
                  </li>
                </ul>
              </div>

              <div className="pt-2 border-t border-gray-100">
                <span className="font-medium text-indigo-700">Deliverables:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc text-xs">
                  <li>
                    <code>src/frontend/src/App.tsx</code> - Integrated SEO provider.
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Session: February 17, 2026 - Document Center Modernization */}
          <div className="border-l-4 border-indigo-500 pl-4 mb-8 bg-indigo-50/20 py-4 rounded-r-xl">
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <SparklesIcon className="w-4 h-4 text-indigo-600" />
                Session: February 17, 2026 - Document Center Modernization
              </h4>
              <span className="text-xs text-gray-500">Current Session</span>
            </div>

            <div className="space-y-3 text-sm text-gray-700">
              <div>
                <span className="font-medium text-blue-700 underline decoration-blue-200 decoration-2">
                  User Request:
                </span>
                <p className="mt-1 text-gray-600 italic">
                  "fix the DOCUMENTS functionality... redesign to a world class standards the forms
                  rendering... make the Manually generated 'bill of sale' interactive (befor
                  printing it, the user must be able to complete the input fields)"
                </p>
              </div>

              <div>
                <span className="font-medium text-green-700">Key Achievements:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li>
                    <span className="font-semibold text-gray-900">Premium UI Redesign:</span>{' '}
                    Replaced the entire Document Center with a high-end "Glassmorphism" interface.
                  </li>
                  <li>
                    <span className="font-semibold text-gray-900">Interactive Form Modal:</span>{' '}
                    Created{' '}
                    <code className="bg-gray-100 px-1 text-[11px]">DocumentFormModal.tsx</code>{' '}
                    which dynamically generates inputs for any document template.
                  </li>
                  <li>
                    <span className="font-semibold text-gray-900">Smart-Fill Enhancement:</span>{' '}
                    Integrated automatic data extraction from vehicles and customers into the manual
                    entry forms.
                  </li>
                  <li>
                    <span className="font-semibold text-gray-900">Unified Experience:</span>{' '}
                    Upgraded both the main Document Center and the Sidebar{' '}
                    <code className="bg-gray-100 px-1 text-[11px]">DocumentGenerator.tsx</code> to
                    use the interactive flow.
                  </li>
                  <li>
                    <span className="font-semibold text-gray-900">Stabilization:</span> Fixed a
                    critical build error where{' '}
                    <code className="bg-gray-100 px-1 text-[11px]">DocumentIcon</code> was undefined
                    in Settings.
                  </li>
                </ul>
              </div>

              <div className="pt-2 border-t border-gray-100">
                <span className="font-medium text-emerald-700">Key Deliverables:</span>
                <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div className="flex items-center gap-2 p-2 bg-white rounded-lg border border-gray-100 shadow-sm">
                    <DocumentTextIcon className="w-4 h-4 text-emerald-500" />
                    <code className="text-[10px] text-gray-600">
                      src/pages/documents/DocumentCenterPage.tsx
                    </code>
                  </div>
                  <div className="flex items-center gap-2 p-2 bg-white rounded-lg border border-gray-100 shadow-sm">
                    <DocumentTextIcon className="w-4 h-4 text-blue-500" />
                    <code className="text-[10px] text-gray-600">
                      src/components/documents/DocumentFormModal.tsx
                    </code>
                  </div>
                  <div className="flex items-center gap-2 p-2 bg-white rounded-lg border border-gray-100 shadow-sm">
                    <DocumentTextIcon className="w-4 h-4 text-purple-500" />
                    <code className="text-[10px] text-gray-600">
                      src/components/documents/DocumentGenerator.tsx
                    </code>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Session: February 17, 2026 */}
          <div className="border-l-4 border-blue-400 pl-4 mb-6">
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-semibold text-gray-900">Session: February 17, 2026</h4>
              <span className="text-xs text-gray-500">(time n/a)</span>
            </div>

            <div className="space-y-3 text-sm text-gray-700">
              <div>
                <span className="font-medium text-blue-700">Initial Issue:</span>
                <p className="mt-1">
                  Documents could only generate/download PDFs; there was no way to save generated
                  documents to a customer record or email them from the system.
                </p>
              </div>

              <div>
                <span className="font-medium text-green-700">Key Fixes Applied:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li>
                    Added a tenant-safe <code className="bg-gray-100 px-1">customer_documents</code>{' '}
                    persistence model + Alembic migration.
                  </li>
                  <li>
                    Implemented backend endpoints to generate+save, list saved docs per customer,
                    secure download, and email PDFs via SMTP.
                  </li>
                  <li>
                    Updated Customer Detail “Quick Documents” to generate+save to the customer, then
                    surface saved docs with Download/Print/Email actions.
                  </li>
                  <li>
                    Added unit tests for the new document endpoints using mocked DB + filesystem.
                  </li>
                  <li>
                    Fixed CarsForSale Import “Test Connection” to send{' '}
                    <code className="bg-gray-100 px-1">ftpPassword</code> so valid credentials no
                    longer fail.
                  </li>
                </ul>
              </div>

              <div>
                <span className="font-medium text-red-700">Discovered Issues:</span>
                <p className="mt-1">
                  Email requires SMTP env vars; without configuration, the API returns a clear error
                  indicating SMTP is not configured.
                </p>
              </div>

              <div className="pt-2 border-t border-gray-200">
                <span className="font-medium text-indigo-700">Key Deliverables:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li>
                    <code className="bg-gray-100 px-1">
                      src/backend/api/v1/endpoints/documents.py
                    </code>{' '}
                    - generate+save/list/download/email endpoints
                  </li>
                  <li>
                    <code className="bg-gray-100 px-1">
                      src/backend/models/customer_document.py
                    </code>{' '}
                    - persistence model
                  </li>
                  <li>
                    <code className="bg-gray-100 px-1">
                      src/backend/alembic/versions/20260217_000100_create_customer_documents_table.py
                    </code>{' '}
                    - migration
                  </li>
                  <li>
                    <code className="bg-gray-100 px-1">
                      src/frontend/src/components/documents/DocumentGenerator.tsx
                    </code>{' '}
                    - saved docs UI + actions
                  </li>
                  <li>
                    <code className="bg-gray-100 px-1">
                      src/frontend/src/components/vehicles/CarsForSaleImportConfig.tsx
                    </code>{' '}
                    - send FTP password on Test Connection
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Session: February 16, 2026 (Production) */}
          <div className="border-l-4 border-blue-400 pl-4 mb-6">
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-semibold text-gray-900">
                Session: February 16, 2026 (Production)
              </h4>
              <span className="text-xs text-gray-500">(time n/a)</span>
            </div>

            <div className="space-y-3 text-sm text-gray-700">
              <div>
                <span className="font-medium text-blue-700">Initial Issue:</span>
                <p className="mt-1">
                  unitedautobrokers.online invoice create page broke after OS update: VIN decode
                  stopped working and Purchase Pricing Calculation stopped updating totals.
                </p>
              </div>

              <div>
                <span className="font-medium text-green-700">Key Fixes Applied:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li>
                    Upgraded Ubuntu packages on the production host and verified Apache + app
                    services were healthy.
                  </li>
                  <li>
                    Replaced failing auto.dev VIN decode (HTTP 401 due to invalid API key) with an
                    NHTSA vPIC fallback in the Laravel repo layer.
                  </li>
                  <li>
                    Fixed vue-numeric crash by replacing incompatible accounting-js include (module
                    is not defined) with a browser-safe accounting.js build providing
                    window.accounting.
                  </li>
                  <li>
                    Added a local accounting shim fallback and switched vue-numeric to a local asset
                    to avoid CDN breakage.
                  </li>
                  <li>
                    Hardened invoice pricing calculations by adding a safe numeric coercion helper
                    (prevents NaN when vue-numeric formats fields) and setting numeric defaults for
                    totals.
                  </li>
                  <li>
                    Adjusted CSS z-index so Total Credit/Deposit action buttons render above the
                    numeric input.
                  </li>
                  <li>
                    Cleared Laravel compiled views so Blade template changes deployed immediately.
                  </li>
                </ul>
              </div>

              <div>
                <span className="font-medium text-red-700">Discovered Issues:</span>
                <p className="mt-1">
                  Root domain was served by an Apache/Laravel app (not the local docker SimpleDealer
                  stack); VIN decode failures traced to an expired/invalid third-party API key
                  returning 401.
                </p>
              </div>

              <div className="pt-2 border-t border-gray-200">
                <span className="font-medium text-indigo-700">Key Deliverables:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li>
                    <code className="bg-gray-100 px-1">
                      /var/www/html/app/Http/Controllers/Repo/InvoiceRepo.php
                    </code>{' '}
                    - VIN decode fallback via NHTSA vPIC
                  </li>
                  <li>
                    <code className="bg-gray-100 px-1">
                      /var/www/html/resources/views/Admin_panel/Invoice/Create.blade.php
                    </code>{' '}
                    - robust Purchase Pricing Calculation logic
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Session: February 16, 2026 */}
          <div className="border-l-4 border-blue-400 pl-4 mb-6">
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-semibold text-gray-900">Session: February 16, 2026</h4>
              <span className="text-xs text-gray-500">(time n/a)</span>
            </div>

            <div className="space-y-3 text-sm text-gray-700">
              <div>
                <span className="font-medium text-blue-700">Initial Issue:</span>
                <p className="mt-1">
                  CarsForSale import config endpoint returned 404; Inventory &gt; Active and Online
                  Marketing vehicle images were not populating.
                </p>
              </div>

              <div>
                <span className="font-medium text-green-700">Key Fixes Applied:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li>
                    Aligned backend router mount to match frontend CarsForSale paths (no more 404 on
                    import config).
                  </li>
                  <li>
                    Fixed FastAPI startup crash caused by invalid{' '}
                    <code className="bg-gray-100 px-1">Request | None</code> parameter annotation.
                  </li>
                  <li>
                    Restored images for Inventory + Marketing by falling back to{' '}
                    <code className="bg-gray-100 px-1">Vehicle.images</code> and normalizing image
                    URLs.
                  </li>
                  <li>
                    Updated Vehicles page thumbnail fallback to route non-http image paths through{' '}
                    <code className="bg-gray-100 px-1">apiService.getFileUrl()</code>.
                  </li>
                </ul>
              </div>

              <div>
                <span className="font-medium text-red-700">Discovered Issues:</span>
                <p className="mt-1">
                  Backend port 8000 appeared “up” but was non-responsive because FastAPI failed
                  during route registration (invalid request type annotation).
                </p>
              </div>

              <div className="pt-2 border-t border-gray-200">
                <span className="font-medium text-indigo-700">Key Deliverables:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li>
                    <code className="bg-gray-100 px-1">src/backend/api/v1/api.py</code> -
                    CarsForSale import router mounted at expected prefix
                  </li>
                  <li>
                    <code className="bg-gray-100 px-1">
                      src/backend/api/v1/endpoints/vehicles.py
                    </code>{' '}
                    - image URL normalization + Vehicle.images fallback
                  </li>
                  <li>
                    <code className="bg-gray-100 px-1">
                      src/backend/api/v1/endpoints/vehicles_dashboard.py
                    </code>{' '}
                    - marketing images fallback + normalization
                  </li>
                  <li>
                    <code className="bg-gray-100 px-1">
                      src/frontend/src/pages/vehicles/VehiclesPage.tsx
                    </code>{' '}
                    - thumbnail fallback supports backend-served paths
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Session: February 16, 2026 (Staging) */}
          <div className="border-l-4 border-blue-400 pl-4 mb-6">
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-semibold text-gray-900">
                Session: February 16, 2026 (Staging)
              </h4>
              <span className="text-xs text-gray-500">(time n/a)</span>
            </div>

            <div className="space-y-3 text-sm text-gray-700">
              <div>
                <span className="font-medium text-blue-700">Initial Issue:</span>
                <p className="mt-1">
                  Frontend login failed on staging with 401/403 after a server rebuild; the server
                  no longer had a real git clone at /opt/SimpleDealer, making deploys and restarts
                  inconsistent.
                </p>
              </div>

              <div>
                <span className="font-medium text-green-700">Key Fixes Applied:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li>
                    Created a fresh /opt/SimpleDealer git clone on Server 1 using a GitHub deploy
                    key and restored .env.prod + compose overrides from backup.
                  </li>
                  <li>
                    Rebuilt and started postgres/redis/backend/frontend via docker compose (kept
                    Apache as the host web server on port 80).
                  </li>
                  <li>
                    Added an Apache vhost for dev.unitedautobrokers.online to proxy to the FastAPI
                    backend (was falling back to the legacy Laravel vhost).
                  </li>
                  <li>
                    Reset admin@demo.com password hash via create_admin_user.py so the canonical
                    credentials work again (Admin$ecure!2026).
                  </li>
                  <li>
                    Stabilized JWT auth across uvicorn workers by setting a persistent
                    JWT_SECRET_KEY (fixes “login then immediate logout” caused by intermittent
                    401s).
                  </li>
                </ul>
              </div>

              <div>
                <span className="font-medium text-red-700">Discovered Issues:</span>
                <p className="mt-1">
                  The docker compose project metadata referenced /opt/SimpleDealer even though the
                  directory was missing; also dev.unitedautobrokers.online had no dedicated Apache
                  vhost so requests hit the default Laravel vhost.
                </p>
              </div>

              <div className="pt-2 border-t border-gray-200">
                <span className="font-medium text-indigo-700">Key Deliverables:</span>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li>
                    <code className="bg-gray-100 px-1">
                      /etc/apache2/sites-available/dev.unitedautobrokers.online.conf
                    </code>{' '}
                    - API reverse proxy to FastAPI backend
                  </li>
                  <li>
                    <code className="bg-gray-100 px-1">
                      src/backend/scripts/create_admin_user.py
                    </code>{' '}
                    - password/role refresh used to restore staging login
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Active Tasks Summary */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-green-700 uppercase tracking-wide">
                ✅ Active Tasks ({overview?.active_tasks?.length ?? 0})
              </h4>
              {overview?.active_tasks && overview.active_tasks.length > 0 ? (
                <ul className="text-sm text-gray-700 space-y-2">
                  {overview.active_tasks.slice(0, 5).map((task) => (
                    <li key={task.id} className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                      <span>
                        <strong>{task.title}</strong>
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-gray-500 italic">
                  No active tasks. Create tasks to track your work.
                </p>
              )}
            </div>

            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-purple-700 uppercase tracking-wide">
                📊 Project Stats
              </h4>
              <ul className="text-sm text-gray-700 space-y-2">
                <li className="flex items-start">
                  <ClockIcon className="h-4 w-4 text-purple-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span>
                    <strong>Project Age:</strong> {kpis?.project_age_days ?? 0} days
                  </span>
                </li>
                <li className="flex items-start">
                  <ClockIcon className="h-4 w-4 text-purple-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span>
                    <strong>Total Tasks:</strong> {kpis?.total_tasks ?? 0}
                  </span>
                </li>
                <li className="flex items-start">
                  <ClockIcon className="h-4 w-4 text-purple-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span>
                    <strong>Completion Rate:</strong> {kpis?.completion_rate ?? 0}%
                  </span>
                </li>
                <li className="flex items-start">
                  <ClockIcon className="h-4 w-4 text-purple-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span>
                    <strong>Open Errors:</strong> {kpis?.open_errors ?? 0}
                  </span>
                </li>
              </ul>
            </div>
          </div>

          {/* Open Errors Alert */}
          {overview?.open_errors && overview.open_errors.length > 0 && (
            <div className="mt-4 p-3 bg-red-50 border-l-4 border-red-400 rounded">
              <div className="flex">
                <ExclamationCircleIcon className="h-5 w-5 text-red-400 mr-2" />
                <div className="text-sm">
                  <p className="font-medium text-red-800">
                    Open Issues ({overview.open_errors.length}):
                  </p>
                  <ul className="text-red-700 mt-1">
                    {overview.open_errors
                      .slice(0, 3)
                      .map((error: { id: string; title: string }) => (
                        <li key={error.id}>• {error.title}</li>
                      ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Troubleshooting Library - Known Recurring Issues */}
      <div className="bg-white shadow rounded-lg border-l-4 border-yellow-400">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center mb-4">
            <div className="flex-shrink-0">
              <ExclamationCircleIcon className="h-6 w-6 text-yellow-600" />
            </div>
            <h3 className="ml-3 text-lg leading-6 font-medium text-gray-900">
              Troubleshooting Library - Known Recurring Issues
            </h3>
          </div>

          <div className="space-y-6">
            {/* Issue #12: Payment Process 422 (Date Format) */}
            <div className="border-l-4 border-amber-400 pl-4">
              <div className="flex justify-between items-start mb-2">
                <h4 className="text-sm font-semibold text-gray-900">
                  ⚠️ Payment Process 422 (RESOLVED)
                </h4>
                <span className="text-xs text-gray-500">Last Occurred: Feb 8, 2026</span>
              </div>

              <div className="space-y-3 text-sm text-gray-700">
                <div>
                  <span className="font-medium text-red-700">Symptoms:</span>
                  <ul className="mt-1 ml-4 space-y-1 list-disc">
                    <li>Payment submission returns 422 Unprocessable Entity</li>
                  </ul>
                </div>

                <div>
                  <span className="font-medium text-blue-700">Root Cause:</span>
                  <p className="mt-1">
                    Backend expects ISO datetime; frontend sent date-only string.
                  </p>
                </div>

                <div>
                  <span className="font-medium text-green-700">Solution:</span>
                  <ul className="mt-1 ml-4 space-y-1 list-disc">
                    <li>
                      Send <code className="bg-gray-100 px-1">YYYY-MM-DDT00:00:00</code> for{' '}
                      <code className="bg-gray-100 px-1">payment_date</code>.
                    </li>
                  </ul>
                </div>

                <div className="pt-2 border-t border-gray-200">
                  <span className="font-medium text-indigo-700">Files Affected:</span>
                  <ul className="mt-1 ml-4 space-y-1 list-disc">
                    <li>
                      <code className="bg-gray-100 px-1">src/frontend/src/services/bhphApi.ts</code>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Issue #11: Payment Search 500 (BHPH Tenant UUID) */}
            <div className="border-l-4 border-amber-400 pl-4">
              <div className="flex justify-between items-start mb-2">
                <h4 className="text-sm font-semibold text-gray-900">
                  ⚠️ Payment Search 500 (RESOLVED)
                </h4>
                <span className="text-xs text-gray-500">Last Occurred: Feb 8, 2026</span>
              </div>

              <div className="space-y-3 text-sm text-gray-700">
                <div>
                  <span className="font-medium text-red-700">Symptoms:</span>
                  <ul className="mt-1 ml-4 space-y-1 list-disc">
                    <li>BHPH payment search returns 500 and shows CORS noise</li>
                  </ul>
                </div>

                <div>
                  <span className="font-medium text-blue-700">Root Cause:</span>
                  <p className="mt-1">
                    BHPH account read schema expected{' '}
                    <code className="bg-gray-100 px-1">tenant_id</code> as string but DB returns
                    UUID.
                  </p>
                </div>

                <div>
                  <span className="font-medium text-green-700">Solution:</span>
                  <ul className="mt-1 ml-4 space-y-1 list-disc">
                    <li>
                      Align <code className="bg-gray-100 px-1">tenant_id</code> types to UUID in
                      BHPH models.
                    </li>
                    <li>Restart backend to reload models.</li>
                  </ul>
                </div>

                <div className="pt-2 border-t border-gray-200">
                  <span className="font-medium text-indigo-700">Files Affected:</span>
                  <ul className="mt-1 ml-4 space-y-1 list-disc">
                    <li>
                      <code className="bg-gray-100 px-1">src/backend/models/bhph_account.py</code>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Issue #9: Inventory Joined (Needs CarsForSale Only) */}
            <div className="border-l-4 border-amber-400 pl-4">
              <div className="flex justify-between items-start mb-2">
                <h4 className="text-sm font-semibold text-gray-900">
                  ⚠️ Inventory Joined (RESOLVED)
                </h4>
                <span className="text-xs text-gray-500">Last Occurred: Feb 8, 2026</span>
              </div>

              <div className="space-y-3 text-sm text-gray-700">
                <div>
                  <span className="font-medium text-red-700">Symptoms:</span>
                  <ul className="mt-1 ml-4 space-y-1 list-disc">
                    <li>Inventory shows mixed datasets (CarsForSale + other imports)</li>
                  </ul>
                </div>

                <div>
                  <span className="font-medium text-blue-700">Root Cause:</span>
                  <p className="mt-1">
                    Multiple imports populated the same inventory table without clear source
                    separation.
                  </p>
                </div>

                <div>
                  <span className="font-medium text-green-700">Solution:</span>
                  <ul className="mt-1 ml-4 space-y-1 list-disc">
                    <li>Truncate inventory tables and reimport the CarsForSale CSV only.</li>
                    <li>
                      Tag CarsForSale rows with{' '}
                      <code className="bg-gray-100 px-1">external_source=carsforsale</code>.
                    </li>
                  </ul>
                </div>

                <div className="pt-2 border-t border-gray-200">
                  <span className="font-medium text-indigo-700">Files Affected:</span>
                  <ul className="mt-1 ml-4 space-y-1 list-disc">
                    <li>
                      <code className="bg-gray-100 px-1">scripts/import_carsforsale_csv.py</code>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Issue #10: BHPH Dashboard Not Using Deal Types */}
            <div className="border-l-4 border-amber-400 pl-4">
              <div className="flex justify-between items-start mb-2">
                <h4 className="text-sm font-semibold text-gray-900">
                  ⚠️ BHPH Dashboard Not Using Deal Types (RESOLVED)
                </h4>
                <span className="text-xs text-gray-500">Last Occurred: Feb 8, 2026</span>
              </div>

              <div className="space-y-3 text-sm text-gray-700">
                <div>
                  <span className="font-medium text-red-700">Symptoms:</span>
                  <ul className="mt-1 ml-4 space-y-1 list-disc">
                    <li>BHPH Dashboard ignores BHPH deal types from Deals list</li>
                  </ul>
                </div>

                <div>
                  <span className="font-medium text-blue-700">Root Cause:</span>
                  <p className="mt-1">
                    Dashboard only loaded CSV data instead of mapping BHPH deals.
                  </p>
                </div>

                <div>
                  <span className="font-medium text-green-700">Solution:</span>
                  <ul className="mt-1 ml-4 space-y-1 list-disc">
                    <li>
                      Use deals endpoint first and map{' '}
                      <code className="bg-gray-100 px-1">deal_type=BHPH</code> into dashboard rows.
                    </li>
                    <li>Fallback to CSV data when no BHPH deals exist.</li>
                  </ul>
                </div>

                <div className="pt-2 border-t border-gray-200">
                  <span className="font-medium text-indigo-700">Files Affected:</span>
                  <ul className="mt-1 ml-4 space-y-1 list-disc">
                    <li>
                      <code className="bg-gray-100 px-1">
                        src/frontend/src/pages/bhph-dashboard/BHPHDashboard.tsx
                      </code>{' '}
                      (DELETED — legacy file removed Feb 11, 2026)
                    </li>
                    <li>
                      <code className="bg-gray-100 px-1">
                        src/frontend/src/pages/deals/DealsPage.tsx
                      </code>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Issue #8: Inventory Images Missing (Uploads Path) */}
            <div className="border-l-4 border-amber-400 pl-4">
              <div className="flex justify-between items-start mb-2">
                <h4 className="text-sm font-semibold text-gray-900">
                  ⚠️ Inventory Images Missing (RESOLVED)
                </h4>
                <span className="text-xs text-gray-500">Last Occurred: Feb 8, 2026</span>
              </div>

              <div className="space-y-3 text-sm text-gray-700">
                <div>
                  <span className="font-medium text-red-700">Symptoms:</span>
                  <ul className="mt-1 ml-4 space-y-1 list-disc">
                    <li>
                      Vehicles load but image URLs 404 at{' '}
                      <code className="bg-gray-100 px-1">/uploads/vehicles/...</code>
                    </li>
                  </ul>
                </div>

                <div>
                  <span className="font-medium text-blue-700">Root Cause:</span>
                  <p className="mt-1">
                    Backend mounted uploads from a path that didn’t match the container uploads
                    volume.
                  </p>
                </div>

                <div>
                  <span className="font-medium text-green-700">Solution:</span>
                  <ul className="mt-1 ml-4 space-y-1 list-disc">
                    <li>
                      Resolve uploads path via <code className="bg-gray-100 px-1">UPLOAD_DIR</code>{' '}
                      in backend and remount static files.
                    </li>
                    <li>Restart backend to apply static mount change.</li>
                  </ul>
                </div>

                <div className="pt-2 border-t border-gray-200">
                  <span className="font-medium text-indigo-700">Files Affected:</span>
                  <ul className="mt-1 ml-4 space-y-1 list-disc">
                    <li>
                      <code className="bg-gray-100 px-1">src/backend/main.py</code>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Issue #7: CarsForSale Import Timestamp Error */}
            <div className="border-l-4 border-amber-400 pl-4">
              <div className="flex justify-between items-start mb-2">
                <h4 className="text-sm font-semibold text-gray-900">
                  ⚠️ CarsForSale Import Fails (RESOLVED)
                </h4>
                <span className="text-xs text-gray-500">Last Occurred: Feb 8, 2026</span>
              </div>

              <div className="space-y-3 text-sm text-gray-700">
                <div>
                  <span className="font-medium text-red-700">Symptoms:</span>
                  <ul className="mt-1 ml-4 space-y-1 list-disc">
                    <li>CarsForSale CSV import rolls back on first insert</li>
                    <li>Asyncpg error: can't subtract offset-naive and offset-aware datetimes</li>
                  </ul>
                </div>

                <div>
                  <span className="font-medium text-blue-700">Root Cause:</span>
                  <p className="mt-1">
                    Import script injected timezone-aware datetimes into SQLAlchemy fields mapped as
                    timestamp without time zone.
                  </p>
                </div>

                <div>
                  <span className="font-medium text-green-700">Solution:</span>
                  <ul className="mt-1 ml-4 space-y-1 list-disc">
                    <li>
                      Use naive UTC timestamps in import script for{' '}
                      <code className="bg-gray-100 px-1">created_at</code> and{' '}
                      <code className="bg-gray-100 px-1">updated_at</code>.
                    </li>
                    <li>Re-run import against the container database.</li>
                  </ul>
                </div>

                <div className="pt-2 border-t border-gray-200">
                  <span className="font-medium text-indigo-700">Files Affected:</span>
                  <ul className="mt-1 ml-4 space-y-1 list-disc">
                    <li>
                      <code className="bg-gray-100 px-1">scripts/import_carsforsale_csv.py</code>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Issue #6: Vehicles 500 / CORS Noise (Enum Mapping) */}
            <div className="border-l-4 border-amber-400 pl-4">
              <div className="flex justify-between items-start mb-2">
                <h4 className="text-sm font-semibold text-gray-900">
                  ⚠️ Vehicles 500 / CORS Noise (RESOLVED)
                </h4>
                <span className="text-xs text-gray-500">Last Occurred: Feb 8, 2026</span>
              </div>

              <div className="space-y-3 text-sm text-gray-700">
                <div>
                  <span className="font-medium text-red-700">Symptoms:</span>
                  <ul className="mt-1 ml-4 space-y-1 list-disc">
                    <li>Inventory list shows “Failed to load vehicles”</li>
                    <li>Console reports CORS error with 500 response</li>
                    <li>Backend logs show Enum lookup error for status</li>
                  </ul>
                </div>

                <div>
                  <span className="font-medium text-blue-700">Root Cause:</span>
                  <p className="mt-1">
                    SQLAlchemy Enum stored enum names (uppercase) while DB check constraint expects
                    lowercase values.
                  </p>
                </div>

                <div>
                  <span className="font-medium text-green-700">Solution:</span>
                  <ul className="mt-1 ml-4 space-y-1 list-disc">
                    <li>
                      Use Enum values with <code className="bg-gray-100 px-1">values_callable</code>{' '}
                      and <code className="bg-gray-100 px-1">native_enum=False</code>.
                    </li>
                    <li>Restart backend to reload ORM mappings.</li>
                  </ul>
                </div>

                <div className="pt-2 border-t border-gray-200">
                  <span className="font-medium text-indigo-700">Files Affected:</span>
                  <ul className="mt-1 ml-4 space-y-1 list-disc">
                    <li>
                      <code className="bg-gray-100 px-1">src/backend/models/vehicle.py</code>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Issue #5: Demo Data Missing After DB Reset */}
            <div className="border-l-4 border-amber-400 pl-4">
              <div className="flex justify-between items-start mb-2">
                <h4 className="text-sm font-semibold text-gray-900">
                  ⚠️ Demo Data Missing After DB Reset (RESOLVED)
                </h4>
                <span className="text-xs text-gray-500">Last Occurred: Feb 8, 2026</span>
              </div>

              <div className="space-y-3 text-sm text-gray-700">
                <div>
                  <span className="font-medium text-red-700">Symptoms:</span>
                  <ul className="mt-1 ml-4 space-y-1 list-disc">
                    <li>Customer list empty in UI</li>
                    <li>Inventory list empty in UI</li>
                    <li>Database counts show 0 rows for customers/vehicles</li>
                  </ul>
                </div>

                <div>
                  <span className="font-medium text-blue-700">Root Cause:</span>
                  <p className="mt-1">
                    Imports ran against a host database, while the backend uses the Docker Postgres
                    container; vehicle status casing failed DB constraints during import.
                  </p>
                </div>

                <div>
                  <span className="font-medium text-green-700">Solution:</span>
                  <ul className="mt-1 ml-4 space-y-1 list-disc">
                    <li>Run import scripts inside the backend container.</li>
                    <li>
                      Ensure vehicle status values are lowercase to satisfy the{' '}
                      <code className="bg-gray-100 px-1">vehicles_status_check</code> constraint.
                    </li>
                    <li>Verify counts in container DB after import.</li>
                  </ul>
                </div>

                <div className="pt-2 border-t border-gray-200">
                  <span className="font-medium text-indigo-700">Files Affected:</span>
                  <ul className="mt-1 ml-4 space-y-1 list-disc">
                    <li>
                      <code className="bg-gray-100 px-1">scripts/import_customers_simple.py</code>
                    </li>
                    <li>
                      <code className="bg-gray-100 px-1">scripts/import_vehicle_inventory.py</code>
                    </li>
                    <li>
                      <code className="bg-gray-100 px-1">data/imported-data/*.csv</code>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Issue #5: Root Domain Loading Legacy App */}
            <div className="border-l-4 border-red-400 pl-4">
              <div className="flex justify-between items-start mb-2">
                <h4 className="text-sm font-semibold text-gray-900">
                  ⚠️ Root Domain Loading Legacy App (SuperDealer)
                </h4>
                <span className="text-xs text-gray-500">Last Occurred: Feb 19, 2026</span>
              </div>

              <div className="space-y-3 text-sm text-gray-700">
                <div>
                  <span className="font-medium text-red-700">Symptoms:</span>
                  <ul className="mt-1 ml-4 space-y-1 list-disc">
                    <li>
                      <code className="bg-gray-100 px-1">
                        https://unitedautobrokers.online/login
                      </code>{' '}
                      loads the old "SuperDealer" site.
                    </li>
                    <li>
                      Subdomains like{' '}
                      <code className="bg-gray-100 px-1">staging.unitedautobrokers.online</code>{' '}
                      work correctly.
                    </li>
                  </ul>
                </div>

                <div>
                  <span className="font-medium text-blue-700">Root Cause:</span>
                  <p className="mt-1">
                    Apache on the host is still configured to serve a legacy Laravel app for the
                    root domain on both port 80 and 443.
                  </p>
                </div>

                <div>
                  <span className="font-medium text-green-700">Solution:</span>
                  <ul className="mt-1 ml-4 space-y-1 list-disc">
                    <li>
                      Updated <code className="bg-gray-100 px-1">scripts/fix_root_domain.sh</code>{' '}
                      to handle HTTPS (443) using Cloudflare certificates.
                    </li>
                    <li>
                      Configure Apache to proxy all root domain traffic to Docker ports{' '}
                      <code className="bg-gray-100 px-1">18080</code> (frontend) and{' '}
                      <code className="bg-gray-100 px-1">18000</code> (backend).
                    </li>
                  </ul>
                </div>

                <div className="pt-2 border-t border-gray-200">
                  <span className="font-medium text-indigo-700">Files Affected:</span>
                  <ul className="mt-1 ml-4 space-y-1 list-disc">
                    <li>
                      <code className="bg-gray-100 px-1">scripts/fix_root_domain.sh</code>
                    </li>
                    <li>
                      <code className="bg-gray-100 px-1">
                        infrastructure/apache/unitedautobrokers.online.conf
                      </code>
                    </li>
                  </ul>
                </div>

                <div className="bg-blue-50 p-3 rounded border border-blue-200">
                  <p className="text-xs font-medium text-blue-800">
                    <strong>⚡ Deployment Command (Run on Server 1):</strong>
                  </p>
                  <code className="block mt-2 bg-white px-2 py-1 text-xs border border-blue-100">
                    cd /opt/SimpleDealer && sudo bash scripts/fix_root_domain.sh
                  </code>
                </div>
              </div>
            </div>

            {/* Issue #4: BHPH Search 500 Error */}
            <div className="border-l-4 border-amber-400 pl-4">
              <div className="flex justify-between items-start mb-2">
                <h4 className="text-sm font-semibold text-gray-900">
                  ⚠️ BHPH Search 500 Error (RESOLVED)
                </h4>
                <span className="text-xs text-gray-500">Last Occurred: Feb 8, 2026</span>
              </div>

              <div className="space-y-3 text-sm text-gray-700">
                <div>
                  <span className="font-medium text-red-700">Symptoms:</span>
                  <ul className="mt-1 ml-4 space-y-1 list-disc">
                    <li>BHPH Account Search returns 500 Internal Server Error</li>
                    <li>Browser console shows CORS error (masked 500)</li>
                    <li>Logs show "DataError: invalid input syntax for type uuid: 'default'"</li>
                  </ul>
                </div>

                <div>
                  <span className="font-medium text-blue-700">Root Cause:</span>
                  <p className="mt-1">
                    Schema drift (missing columns) + sending "default" string to Postgres UUID
                    column in <code className="bg-gray-100 px-1">bhph_accounts</code> queries.
                  </p>
                </div>

                <div>
                  <span className="font-medium text-green-700">Solution:</span>
                  <ul className="mt-1 ml-4 space-y-1 list-disc">
                    <li>
                      Added missing columns (<code className="bg-gray-100 px-1">account_id</code>,
                      etc.) via migration.
                    </li>
                    <li>
                      Sanitized <code className="bg-gray-100 px-1">tenant_id</code> query param from
                      "default" to valid UUID in backend endpoints.
                    </li>
                  </ul>
                </div>

                <div className="pt-2 border-t border-gray-200">
                  <span className="font-medium text-indigo-700">Files Affected:</span>
                  <ul className="mt-1 ml-4 space-y-1 list-disc">
                    <li>
                      <code className="bg-gray-100 px-1">
                        src/backend/api/v1/endpoints/bhph_accounts.py
                      </code>
                    </li>
                    <li>
                      <code className="bg-gray-100 px-1">
                        src/backend/alembic/versions/78d04203a75d...
                      </code>{' '}
                      (New)
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Issue #3: Bill of Sale PDF 500 */}
            <div className="border-l-4 border-amber-400 pl-4">
              <div className="flex justify-between items-start mb-2">
                <h4 className="text-sm font-semibold text-gray-900">
                  ⚠️ Bill of Sale PDF 500 (RESOLVED)
                </h4>
                <span className="text-xs text-gray-500">Last Occurred: Feb 6, 2026</span>
              </div>

              <div className="space-y-3 text-sm text-gray-700">
                <div>
                  <span className="font-medium text-red-700">Symptoms:</span>
                  <ul className="mt-1 ml-4 space-y-1 list-disc">
                    <li>Bill of Sale download returns 500</li>
                    <li>
                      Console shows{' '}
                      <code className="bg-gray-100 px-1">
                        /api/v1/deals/{'{'}id{'}'}/documents/download?type=bill_of_sale
                      </code>{' '}
                      failure
                    </li>
                  </ul>
                </div>

                <div>
                  <span className="font-medium text-blue-700">Root Cause:</span>
                  <p className="mt-1">
                    <code className="bg-gray-100 px-1">deal_customer_info.tenant_id</code> column
                    missing in database.
                  </p>
                </div>

                <div>
                  <span className="font-medium text-green-700">Solution:</span>
                  <ul className="mt-1 ml-4 space-y-1 list-disc">
                    <li>
                      Add <code className="bg-gray-100 px-1">tenant_id</code>,{' '}
                      <code className="bg-gray-100 px-1">created_at</code>, and{' '}
                      <code className="bg-gray-100 px-1">updated_at</code> columns to{' '}
                      <code className="bg-gray-100 px-1">deal_customer_info</code>.
                    </li>
                  </ul>
                </div>

                <div className="pt-2 border-t border-gray-200">
                  <span className="font-medium text-indigo-700">Files Affected:</span>
                  <ul className="mt-1 ml-4 space-y-1 list-disc">
                    <li>
                      <code className="bg-gray-100 px-1">
                        src/backend/models/deal_customer_info.py
                      </code>
                    </li>
                    <li>
                      <code className="bg-gray-100 px-1">
                        src/backend/api/v1/endpoints/deal_contracting.py
                      </code>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Issue #2: Dashboard deals CORS noise from 500 */}
            <div className="border-l-4 border-amber-400 pl-4">
              <div className="flex justify-between items-start mb-2">
                <h4 className="text-sm font-semibold text-gray-900">
                  ⚠️ Dashboard Deals 500 / CORS Noise (RESOLVED)
                </h4>
                <span className="text-xs text-gray-500">Last Occurred: Feb 6, 2026</span>
              </div>

              <div className="space-y-3 text-sm text-gray-700">
                <div>
                  <span className="font-medium text-red-700">Symptoms:</span>
                  <ul className="mt-1 ml-4 space-y-1 list-disc">
                    <li>
                      Dashboard fetch to <code className="bg-gray-100 px-1">/api/v1/deals/</code>{' '}
                      returns 500
                    </li>
                    <li>
                      Browser reports CORS error (missing{' '}
                      <code className="bg-gray-100 px-1">Access-Control-Allow-Origin</code>)
                    </li>
                  </ul>
                </div>

                <div>
                  <span className="font-medium text-blue-700">Root Cause:</span>
                  <p className="mt-1">
                    Deal model pointed at legacy <code className="bg-gray-100 px-1">deal</code>{' '}
                    table while queries referenced <code className="bg-gray-100 px-1">deals</code>.
                  </p>
                </div>

                <div>
                  <span className="font-medium text-green-700">Solution:</span>
                  <ul className="mt-1 ml-4 space-y-1 list-disc">
                    <li>
                      Set <code className="bg-gray-100 px-1">Deal.__tablename__</code> to{' '}
                      <code className="bg-gray-100 px-1">deals</code>.
                    </li>
                    <li>
                      Update tenant filters to use{' '}
                      <code className="bg-gray-100 px-1">deals.tenant_id</code>.
                    </li>
                  </ul>
                </div>

                <div className="pt-2 border-t border-gray-200">
                  <span className="font-medium text-indigo-700">Files Affected:</span>
                  <ul className="mt-1 ml-4 space-y-1 list-disc">
                    <li>
                      <code className="bg-gray-100 px-1">src/backend/models/deal.py</code> - table
                      name alignment
                    </li>
                    <li>
                      <code className="bg-gray-100 px-1">
                        src/backend/api/v1/endpoints/deals.py
                      </code>{' '}
                      - tenant filter update
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Issue #1: Vehicle Search Status Filter Case Mismatch */}
            <div className="border-l-4 border-red-400 pl-4">
              <div className="flex justify-between items-start mb-2">
                <h4 className="text-sm font-semibold text-gray-900">
                  ⚠️ Deal Calculator Search Not Working (RECURRING - 3x)
                </h4>
                <span className="text-xs text-gray-500">Last Occurred: Jan 29, 2026</span>
              </div>

              <div className="space-y-3 text-sm text-gray-700">
                <div>
                  <span className="font-medium text-red-700">Symptoms:</span>
                  <ul className="mt-1 ml-4 space-y-1 list-disc">
                    <li>Customer search dropdown returns no results</li>
                    <li>Vehicle search dropdown returns no results</li>
                    <li>Database has data but searches fail</li>
                  </ul>
                </div>

                <div>
                  <span className="font-medium text-blue-700">Root Causes:</span>
                  <ul className="mt-1 ml-4 space-y-1 list-disc">
                    <li>
                      <strong>Customer Issue:</strong> CSV file exists but not imported to database
                    </li>
                    <li>
                      <strong>Vehicle Issue:</strong> Status filter case mismatch (
                      <code className="bg-gray-100 px-1">status=AVAILABLE</code> vs database enum{' '}
                      <code className="bg-gray-100 px-1">available</code>)
                    </li>
                  </ul>
                </div>

                <div>
                  <span className="font-medium text-green-700">Solutions:</span>
                  <ul className="mt-1 ml-4 space-y-1 list-disc">
                    <li>
                      <strong>Customer Fix:</strong> Run{' '}
                      <code className="bg-gray-100 px-1">
                        python3 scripts/import_customers_simple.py
                      </code>
                    </li>
                    <li>
                      <strong>Vehicle Fix:</strong> Change all status filters to{' '}
                      <code className="bg-gray-100 px-1">available</code> (lowercase)
                    </li>
                    <li>
                      <strong>Verify:</strong> Check database enum values:{' '}
                      <code className="bg-gray-100 px-1">
                        SELECT enumlabel FROM pg_enum WHERE enumtypid = 'vehiclestatus'::regtype
                      </code>
                    </li>
                  </ul>
                </div>

                <div>
                  <span className="font-medium text-purple-700">Prevention:</span>
                  <p className="mt-1">
                    Create type-safe enum constants in{' '}
                    <code className="bg-gray-100 px-1">src/types/enums.ts</code> and use in all API
                    calls instead of hardcoded strings.
                  </p>
                </div>

                <div className="pt-2 border-t border-gray-200">
                  <span className="font-medium text-indigo-700">Files Affected:</span>
                  <ul className="mt-1 ml-4 space-y-1 list-disc">
                    <li>
                      <code className="bg-gray-100 px-1">DealCalculatorPage.tsx:515</code> - Fixed
                      Jan 29, 2026 (commit <code className="bg-gray-100 px-1">0b428f9</code>)
                    </li>
                    <li>
                      <code className="bg-gray-100 px-1">VehiclesPage.tsx</code> - Fixed Jan 25,
                      2026
                    </li>
                    <li>Database enum definitions - Fixed Jan 22, 2026</li>
                  </ul>
                </div>

                <div className="bg-yellow-50 p-3 rounded border border-yellow-200">
                  <p className="text-xs font-medium text-yellow-800">
                    <strong>⚡ Quick Fix Checklist:</strong>
                  </p>
                  <ol className="text-xs text-yellow-700 mt-2 ml-4 space-y-1 list-decimal">
                    <li>
                      Check customer count:{' '}
                      <code className="bg-white px-1">SELECT COUNT(*) FROM customers;</code>
                    </li>
                    <li>If 0 customers: Run import script</li>
                    <li>Check vehicle status values in code (must be lowercase)</li>
                    <li>Verify database enum: Must match exactly (case-sensitive)</li>
                  </ol>
                </div>
              </div>
            </div>

            {/* Placeholder for future recurring issues */}
            <div className="text-center py-4 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-sm text-gray-500">
                Additional recurring issues will be documented here as they are identified
              </p>
              <p className="text-xs text-gray-400 mt-1">
                See <code className="bg-gray-100 px-1">docs/SESSION_STORYBOOK.md</code> for complete
                troubleshooting history
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Last storybook sync: Feb 11, 2026 — PM#2: Debug console.log purge (73→0 across 14
                files), OG/Twitter meta tags, per-page ErrorBoundary wraps, PWA manifest polish.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Session Runs */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center mb-4">
            <div className="flex-shrink-0">
              <ChatBubbleLeftRightIcon className="h-6 w-6 text-gray-400" />
            </div>
            <h3 className="ml-3 text-lg leading-6 font-medium text-gray-900">
              Recent Agent Sessions
            </h3>
          </div>

          <div className="space-y-4">
            {overview?.recent_runs && overview.recent_runs.length > 0 ? (
              overview.recent_runs.map(
                (run: {
                  id: string;
                  agent: string;
                  summary: string;
                  started_at: string;
                  status: string;
                }) => (
                  <div key={run.id} className="border-l-4 border-blue-400 pl-4 py-2">
                    <div className="flex justify-between items-start mb-1">
                      <h4 className="text-sm font-semibold text-gray-900">{run.agent}</h4>
                      <span className="text-xs text-gray-500">
                        {new Date(run.started_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700">{run.summary}</p>
                    <span
                      className={`inline-block mt-1 text-xs px-2 py-0.5 rounded ${
                        run.status === 'success'
                          ? 'bg-green-100 text-green-700'
                          : run.status === 'failed'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {run.status}
                    </span>
                  </div>
                )
              )
            ) : (
              <div className="text-center py-8 text-gray-500">
                <ChatBubbleLeftRightIcon className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                <p className="text-sm">No session logs yet.</p>
                <p className="text-xs mt-1">
                  Click "Log Session" above to record your agent session summaries.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperTrackerPage;
